import axios, { AxiosInstance, AxiosResponse } from 'axios';
import { csrfTokenManager } from '../utils/csrfTokenManager';
import { oauthStateManager } from '../utils/oauthStateManager';
import Logger from '../utils/logger';
import i18n from '../i18n';
import {
  AuthResponse,
  LoginRequest,
  LoginResult,
  DeviceVerifyRequiredResponse,
  MfaEnrollResponse,
  MfaStatus,
  TrustedDevicesListResponse,
  User,
  UserCreate,
  UserVerification,
  VerificationResponse,
  PasswordResetRequest,
  PasswordResetResponse,
  PasswordResetConfirm,
  Project,
  ProjectCreate,
  Environment,
  EnvironmentCreate,
  EnvironmentUpdate,
  EnvironmentStatus,
  Deployment,
  DeploymentDetail,
  DeploymentUpdate,
  KeycloakEligibleDatabases,
  BuiltImagesResponse,
  CodeApplicationCreate,
  ERPApplicationCreate,
  AppMetrics,
  ProjectMember,
  ProjectMemberCreate,
  ProjectMemberUpdate,
  ProjectEffectiveMember,
  ProjectGroup,
  ProjectRole,
  Group,
  GroupCreate,
  IdentityProviderConfig,
  IdentityProviderUpsert,
  OrgDomain,
  DomainVerifyResponse,
  LoginPolicy,
  LoginPolicyUpdate,
  SsoDiscoverResponse,
  ScimTokenResponse,
  ScimSyncStatus,
  GroupUpdate,
  GroupMember,
  GroupProject,
  AddMemberResponse,
  InvitableMember,
  Invitation,
  InvitationAcceptRequest,
  InvitationAcceptResponse,
  InvitationInfo,
  // Pricing types
  PricingCalculationRequest,
  PricingCalculationResponse,
  AllApplicationPresetsResponse,
  PresetRecommendationRequest,
  RecommendedPresetResponse,
  UserPricingUsageResponse,
  ProjectEstimationResponse,
  PricingComparisonResponse,
  // OAuth & Repository types
  ConnectedRepository,
  QuickConnectRequest,
  QuickConnectResponse,
  // Build types
  BuildRequest,
  BuildResponse,
  BuildStatus,
  BuildLogs,
  // Build Configuration types (Webhook Auto-Build)
  BuildConfiguration,
  BuildConfigurationCreate,
  BuildConfigurationUpdate,
  BuildConfigurationListResponse,
  BuildTriggerRequest,
  BuildTriggerResponse,
  // Image Import types (NubiImport)
  ImageImportRequest,
  ImageImportResponse,
  ImportStatus,
  ImportLogs,
  ValidateImageRequest,
  ValidateImageResponse,
  StoreCredentialsRequest,
  StoreCredentialsResponse,
  CredentialListResponse,
  // Odoo Module types
  OdooModuleAdd,
  OdooModule,
  OdooModuleListResponse,
  OdooModuleLogsResponse,
  // Backup & Restore types
  BackupConfiguration,
  BackupConfigurationCreate,
  Backup,
  BackupListResponse,
  CreateOnDemandBackupResponse,
  RestoreOperation,
  RestoreListResponse,
  RestoreValidateRequest,
  // Organisation types
  Organisation,
  OrganisationCreate,
  OrganisationUpdate,
  OrganisationDeleteRequest,
  OrganisationRestoreResponse,
  OrganisationMember,
  OrganisationMemberCreate,
  OrganisationMemberUpdate,
  InviteMemberResponse,
  OrganisationInvitation,
  OrganisationInvitationInfo,
  OrganisationInvitationAcceptRequest,
  OrganisationInvitationAcceptResponse,
  OrganisationQuotaUsage,
  AvailablePlansResponse,
  PlanChangeRequest,
  EnterpriseRequestData,
  EnterpriseRequestResponse,
  // Prometheus Metrics types
  PrometheusHealthResponse,
  CPUMetricsResponse,
  MemoryMetricsResponse,
  NetworkMetricsResponse,
  StorageMetricsResponse,
  AllMetricsResponse,
  // Asset Transfer types (Organisation V2)
  RemoveMemberResponse,
  RemoveMemberPreviewResponse,
  DeleteAccountResponse,
  DeleteAccountPreviewResponse,
  // Migration & Job Logs types
  MigrationLogsResponse,
  ApplicationJobsResponse,
  JobLogsResponse,
  // Available Updates (Go Live)
  AvailableUpdatesResponse,
  // SQL Editor types
  SqlExecuteResponse,
  SqlResultResponse,
  StudioSessionResponse,
  // Console Kafka
  KafkaSessionResponse,
  // Audit Log types
  AuditLogListResponse,
  AuditLogFilters,
  // Region types
  Region,
  RegionCreate,
  RegionUpdate,
  GenerateInstallScriptResponse,
  RegionConnectionInfo,
  RegionManualCallbackRequest,
  // Blueprint / Stack types
  StackPlan,
  StackSummary,
  DetectRequest,
  DetectResponse,
  // Config files
  ConfigFile,
  ConfigFileInput,
  NubieAiDirection,
  NubieAiContact,
  NubieAiSiteResume,
  NubieAiSiteDetail,
  NubieAiMedia,
  NubieAiResultatMessage,
  NubieAiEtatEnLigne,
  NubieAiTour,
  NubieAiResultatPublication,
  NubieAiSocle,
  McpCapability,
  McpSecretEntry,
  McpConfirmation,
  McpAutorisation,
  McpGrant,
  McpStatus,
  McpToken,
  McpTokenIssued,
  DeploymentBySource,
  AutoDeployState,
} from '../types/api';
import type {
  GoogleAuthorizeResponse,
  GoogleLinkRequiredResponse,
  IdentitiesResponse,
} from '../types/api';

// Configuration de base de l'API
const API_BASE_URL =  window._env_?.REACT_APP_API_URL || 'http://localhost:8000';

// Internal types for API responses
interface GitRepository {
  id: number | string;
  name: string;
  full_name: string;
  clone_url: string;
  html_url: string;
  default_branch: string;
  private: boolean;
  owner?: {
    login: string;
    avatar_url?: string;
  };
}

interface GitUserInfo {
  id: number | string;
  login: string;
  name?: string;
  email?: string;
  avatar_url?: string;
}

interface GitRepositoryResponse {
  id: number;
  repository_name: string;
  repository_owner: string;
  repository_url: string;
  clone_url: string;
  default_branch: string;
  monitored_branch: string;
  webhook_enabled: boolean;
  is_active: boolean;
  connected_at: string;
  provider: string;
}

interface EnvironmentConfig {
  namespace: string;
  gitlab_config?: Record<string, unknown>;
  kubernetes?: Record<string, unknown>;
  argocd?: Record<string, unknown>;
}

interface SyncDeploymentResponse {
  message: string;
  status: string;
  synced_at?: string;
}

interface RestartDeploymentResponse {
  message: string;
  status: string;
  restarted_at?: string;
}

interface DeploymentMetrics {
  cpu_usage: number;
  memory_usage: number;
  pod_count: number;
  restart_count: number;
  timestamp: string;
}

interface BuildpackInfo {
  id: string;
  name: string;
  language: string;
  version?: string;
  description?: string;
}

// Note: This is a flexible type for API responses - consumers may have their own typed interfaces
interface ApiInstanceTypeResponse {
  id: string;
  name: string;
  slug?: string;
  cpu: number;
  ram?: number;
  ram_gb?: number;
  storage?: number;
  price_per_hour?: number;
  description?: string;
  active?: boolean;
}

interface UserBuildsResponse {
  builds: Array<{
    build_id: string;
    status: string;
    created_at: string;
    image_url?: string;
  }>;
  total: number;
}

// AllUserBackupsResponse matches the structure from BackupsPage
interface BackupItem {
  id: string;
  deployment_id: string;
  backup_name: string;
  status: string;
  backup_type: string;
  size_bytes: number | null;
  s3_path: string;
  started_at: string | null;
  completed_at: string | null;
  error_message: string | null;
  backup_timestamp: string;
}

interface DeploymentWithBackups {
  deployment_id: string;
  deployment_name: string;
  application_type: string;
  backups: BackupItem[];
}

interface EnvironmentWithBackups {
  environment_id: string;
  environment_name: string;
  project_id: string;
  project_name: string;
  deployments: DeploymentWithBackups[];
}

interface AllUserBackupsResponse {
  environments: EnvironmentWithBackups[];
  total_backups: number;
}

interface OrganisationMemberUpdateResponse {
  message: string;
  member: {
    id: string;
    role_organisation: string;
  };
}

interface IntelligentBuildApiResponse {
  success: boolean;
  build_id: string;
  message: string;
  analysis?: Record<string, unknown>;
  build_strategy?: Record<string, unknown>;
}

class ApiService {
  private client: AxiosInstance;
  private token: string | null = null;

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
      },
      withCredentials: true, // Send httpOnly cookies automatically
    });

    // Intercepteur pour ajouter le CSRF token (auth via httpOnly cookie)
    this.client.interceptors.request.use((config) => {
      // Add CSRF token for state-changing requests
      const method = config.method?.toUpperCase();
      if (method && ['POST', 'PUT', 'DELETE', 'PATCH'].includes(method)) {
        const csrfHeaders = csrfTokenManager.getHeaders();
        Object.assign(config.headers, csrfHeaders);
      }

      return config;
    });

    // Intercepteur pour gérer les erreurs
    this.client.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          // Compte suspendu : coupe a la requete suivante par l'API (detail
          // account_suspended). Dire pourquoi sur /login plutot qu'un simple retour.
          if (error.response?.data?.detail === 'account_suspended') {
            sessionStorage.setItem('loginError', i18n.t('error.ACCOUNT_SUSPENDED', { ns: 'messages' }));
          }
          // Cookie expiré, nettoyer et rediriger vers la connexion
          this.clearAuthState();
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
    );

    // Vérifier l'état d'authentification au démarrage
    const isAuth = localStorage.getItem('auth_status');
    if (isAuth === 'true') {
      this.token = '__cookie__'; // Marker: auth via httpOnly cookie
    }
  }

  // Authentification
  async login(credentials: LoginRequest): Promise<LoginResult> {
    const response: AxiosResponse<LoginResult> = await this.client.post('/api/v1/auth/login', credentials);
    const data = response.data;
    // The backend returns one of:
    //   - {access_token, token_type}                            → cookie posted, auth on
    //   - {requires_mfa: true, mfa_token: ...}                  → must POST /mfa/login-verify
    //   - {requires_device_verification: true, verify_token, …} → must POST /device/verify
    if ((data as { requires_mfa?: boolean }).requires_mfa) {
      return data;
    }
    if ((data as { requires_device_verification?: boolean }).requires_device_verification) {
      return data;
    }
    this.token = '__cookie__';
    localStorage.setItem('auth_status', 'true');
    return data;
  }

  // ----- Device verification (email-based step-up auth) ----------------------

  async verifyDevice(verifyToken: string, code: string, remember: boolean): Promise<AuthResponse> {
    const r: AxiosResponse<AuthResponse & { trusted_device_issued?: boolean }> = await this.client.post(
      '/api/v1/auth/device/verify',
      { verify_token: verifyToken, code, remember },
    );
    this.token = '__cookie__';
    localStorage.setItem('auth_status', 'true');
    return r.data;
  }

  async resendDeviceCode(verifyToken: string): Promise<DeviceVerifyRequiredResponse> {
    const r = await this.client.post<DeviceVerifyRequiredResponse>(
      '/api/v1/auth/device/resend',
      { verify_token: verifyToken },
    );
    return r.data;
  }

  // ----- Connexion Google (OIDC) et portes du compte -------------------------

  /** L'API a deja pose le cookie (retour Google `status=ok`) : marquer la session cote client. */
  markCookieSession(): void {
    this.token = '__cookie__';
    localStorage.setItem('auth_status', 'true');
  }

  async getGoogleAuthorizeUrl(returnTo?: string): Promise<GoogleAuthorizeResponse> {
    const r = await this.client.get<GoogleAuthorizeResponse>('/api/v1/auth/google/authorize', {
      params: returnTo ? { return_to: returnTo } : undefined,
    });
    return r.data;
  }

  /**
   * Code a 6 chiffres recu par courriel pour lier Google a un compte existant.
   * Meme union de retour que login() : Token | MFA | verification d'appareil.
   */
  async confirmGoogleLink(linkToken: string, code: string): Promise<LoginResult> {
    const r: AxiosResponse<LoginResult> = await this.client.post('/api/v1/auth/google/link/confirm', {
      link_token: linkToken,
      code,
    });
    const data = r.data;
    if ((data as { requires_mfa?: boolean }).requires_mfa) return data;
    if ((data as { requires_device_verification?: boolean }).requires_device_verification) return data;
    this.markCookieSession();
    return data;
  }

  async resendGoogleLinkCode(linkToken: string): Promise<GoogleLinkRequiredResponse> {
    const r = await this.client.post<GoogleLinkRequiredResponse>('/api/v1/auth/google/link/resend', {
      link_token: linkToken,
    });
    return r.data;
  }

  async listIdentities(): Promise<IdentitiesResponse> {
    const r = await this.client.get<IdentitiesResponse>('/api/v1/auth/identities');
    return r.data;
  }

  async unlinkIdentity(identityId: string): Promise<{ unlinked: boolean }> {
    const r = await this.client.delete<{ unlinked: boolean }>(`/api/v1/auth/identities/${identityId}`);
    return r.data;
  }

  /** Depuis le profil : lier Google au compte connecte (la session est la preuve). */
  // ----- SSO Entra ID (plan B palier 2) -------------------------------------

  async ssoDiscover(email: string): Promise<SsoDiscoverResponse> {
    const r = await this.client.post<SsoDiscoverResponse>('/api/v1/auth/sso/discover', { email });
    return r.data;
  }

  async getEntraAuthorizeUrl(organisationId: string, returnTo?: string, loginHint?: string): Promise<GoogleAuthorizeResponse> {
    const params: Record<string, string> = {};
    if (returnTo) params.return_to = returnTo;
    if (loginHint) params.login_hint = loginHint;
    const r = await this.client.get<GoogleAuthorizeResponse>(`/api/v1/auth/entra/${organisationId}/authorize`, { params });
    return r.data;
  }

  async startEntraLink(organisationId: string): Promise<GoogleAuthorizeResponse> {
    const r = await this.client.post<GoogleAuthorizeResponse>(`/api/v1/auth/identities/entra/${organisationId}/link`);
    return r.data;
  }

  async getIdentityProvider(organisationId: string): Promise<IdentityProviderConfig> {
    const r = await this.client.get<IdentityProviderConfig>(`/api/v1/organisations/${organisationId}/identity-provider`);
    return r.data;
  }

  async upsertIdentityProvider(organisationId: string, data: IdentityProviderUpsert): Promise<IdentityProviderConfig> {
    const r = await this.client.put<IdentityProviderConfig>(`/api/v1/organisations/${organisationId}/identity-provider`, data);
    return r.data;
  }

  async deleteIdentityProvider(organisationId: string): Promise<void> {
    await this.client.delete(`/api/v1/organisations/${organisationId}/identity-provider`);
  }

  async listOrgDomains(organisationId: string): Promise<OrgDomain[]> {
    const r = await this.client.get<OrgDomain[]>(`/api/v1/organisations/${organisationId}/domains`);
    return r.data;
  }

  async addOrgDomain(organisationId: string, domain: string): Promise<OrgDomain> {
    const r = await this.client.post<OrgDomain>(`/api/v1/organisations/${organisationId}/domains`, { domain });
    return r.data;
  }

  async verifyOrgDomain(organisationId: string, domainId: string): Promise<DomainVerifyResponse> {
    const r = await this.client.post<DomainVerifyResponse>(`/api/v1/organisations/${organisationId}/domains/${domainId}/verify`);
    return r.data;
  }

  async deleteOrgDomain(organisationId: string, domainId: string): Promise<void> {
    await this.client.delete(`/api/v1/organisations/${organisationId}/domains/${domainId}`);
  }

  async getLoginPolicy(organisationId: string): Promise<LoginPolicy> {
    const r = await this.client.get<LoginPolicy>(`/api/v1/organisations/${organisationId}/login-policy`);
    return r.data;
  }

  async updateLoginPolicy(organisationId: string, data: LoginPolicyUpdate): Promise<LoginPolicy> {
    const r = await this.client.put<LoginPolicy>(`/api/v1/organisations/${organisationId}/login-policy`, data);
    return r.data;
  }


  // ------------------------------------------------------------- Serveur MCP
  async listMcpTokens(organisationId: string): Promise<McpToken[]> {
    const r = await this.client.get<McpToken[]>(`/api/v1/organisations/${organisationId}/mcp/tokens`);
    return r.data;
  }

  async issueMcpToken(
    organisationId: string,
    data: { label?: string; capabilities: McpCapability[]; project_id?: string },
  ): Promise<McpTokenIssued> {
    const r = await this.client.post<McpTokenIssued>(`/api/v1/organisations/${organisationId}/mcp/tokens`, data);
    return r.data;
  }

  async revokeMcpToken(organisationId: string, tokenId: string): Promise<void> {
    await this.client.delete(`/api/v1/organisations/${organisationId}/mcp/tokens/${tokenId}`);
  }

  async getMcpStatus(organisationId: string): Promise<McpStatus> {
    const r = await this.client.get<McpStatus>(`/api/v1/organisations/${organisationId}/mcp/status`);
    return r.data;
  }

  // Les sessions OAuth, a cote des jetons. Les deux chemins cohabitent en permanence :
  // un agent en terminal n'a pas de navigateur pour un aller-retour OAuth.
  async listMcpSessions(organisationId: string): Promise<McpGrant[]> {
    const r = await this.client.get<McpGrant[]>(`/api/v1/organisations/${organisationId}/mcp/sessions`);
    return r.data;
  }

  // La revocation est IMMEDIATE : les jetons sont opaques et relus en base a chaque
  // appel, donc rien ne leur survit.
  async revokeMcpSession(organisationId: string, sessionId: string): Promise<void> {
    await this.client.delete(`/api/v1/organisations/${organisationId}/mcp/sessions/${sessionId}`);
  }

  // Le consentement OAuth. L'API rend le meme 404 qu'une demande introuvable si le
  // compte connecte n'est pas celui qui l'a ouverte en premier.
  async getMcpAutorisation(identifiant: string): Promise<McpAutorisation> {
    const r = await this.client.get<McpAutorisation>(`/api/v1/mcp/autorisations/${identifiant}`);
    return r.data;
  }

  // Rend l'adresse ou renvoyer la personne. Rien n'est emis ici : la decision produit
  // un code a usage unique, que le client echangera lui-meme contre un jeton.
  async decideMcpAutorisation(
    identifiant: string,
    decision: { accorde: boolean; organisation_id?: string; capacites?: McpCapability[] },
  ): Promise<{ redirect_url: string }> {
    const r = await this.client.post<{ redirect_url: string }>(
      `/api/v1/mcp/autorisations/${identifiant}`, decision);
    return r.data;
  }

  // La saisie d'un secret demandee par un agent. L'API rend le meme 404 qu'une demande
  // introuvable si le compte connecte n'est pas celui pour qui elle a ete emise.
  async getMcpSecretEntry(entryId: string): Promise<McpSecretEntry> {
    const r = await this.client.get<McpSecretEntry>(`/api/v1/mcp/secret-entries/${entryId}`);
    return r.data;
  }

  async submitMcpSecretEntry(entryId: string, valeurs: Record<string, string>): Promise<void> {
    await this.client.post(`/api/v1/mcp/secret-entries/${entryId}`, { valeurs });
  }

  // L'approbation d'une operation demandee par un agent. Meme 404 qu'une demande
  // introuvable si le compte connecte n'est pas celui pour qui elle a ete emise.
  async getMcpConfirmation(requestId: string): Promise<McpConfirmation> {
    const r = await this.client.get<McpConfirmation>(`/api/v1/mcp/confirmations/${requestId}`);
    return r.data;
  }

  // N'execute RIEN : enregistre la decision. L'agent la constate a son prochain rejeu.
  async decideMcpConfirmation(requestId: string, approuve: boolean): Promise<void> {
    await this.client.post(`/api/v1/mcp/confirmations/${requestId}`, { approuve });
  }

  async issueScimToken(organisationId: string): Promise<ScimTokenResponse> {
    const r = await this.client.post<ScimTokenResponse>(`/api/v1/organisations/${organisationId}/identity-provider/scim-token`);
    return r.data;
  }

  async revokeScimToken(organisationId: string): Promise<void> {
    await this.client.delete(`/api/v1/organisations/${organisationId}/identity-provider/scim-token`);
  }

  async getScimSyncStatus(organisationId: string): Promise<ScimSyncStatus> {
    const r = await this.client.get<ScimSyncStatus>(`/api/v1/organisations/${organisationId}/identity-provider/sync-status`);
    return r.data;
  }

  async startGoogleLink(): Promise<GoogleAuthorizeResponse> {
    const r = await this.client.post<GoogleAuthorizeResponse>('/api/v1/auth/identities/google/link');
    return r.data;
  }

  async listTrustedDevices(): Promise<TrustedDevicesListResponse> {
    const r = await this.client.get<TrustedDevicesListResponse>('/api/v1/auth/devices');
    return r.data;
  }

  async revokeTrustedDevice(deviceId: string): Promise<{ revoked: boolean }> {
    const r = await this.client.delete<{ revoked: boolean }>(`/api/v1/auth/devices/${deviceId}`);
    return r.data;
  }

  async revokeAllOtherDevices(): Promise<{ revoked_count: number }> {
    const r = await this.client.post<{ revoked_count: number }>(
      '/api/v1/auth/devices/revoke-all-others',
    );
    return r.data;
  }

  // ----- MFA -----------------------------------------------------------------

  async getMfaStatus(): Promise<MfaStatus> {
    const r = await this.client.get<MfaStatus>('/api/v1/auth/mfa/status');
    return r.data;
  }

  async startMfaEnroll(): Promise<MfaEnrollResponse> {
    const r = await this.client.post<MfaEnrollResponse>('/api/v1/auth/mfa/enroll');
    return r.data;
  }

  async verifyMfaEnroll(enrollmentToken: string, code: string): Promise<void> {
    await this.client.post('/api/v1/auth/mfa/verify-enroll', {
      enrollment_token: enrollmentToken,
      code,
    });
  }

  async completeMfaLogin(mfaToken: string, code: string): Promise<AuthResponse> {
    const r: AxiosResponse<AuthResponse> = await this.client.post('/api/v1/auth/mfa/login-verify', {
      mfa_token: mfaToken,
      code,
    });
    this.token = '__cookie__';
    localStorage.setItem('auth_status', 'true');
    return r.data;
  }

  async disableMfa(motDePasse: string, code: string): Promise<void> {
    await this.client.post('/api/v1/auth/mfa/disable', { mot_de_passe: motDePasse, code });
  }

  // ----- Platform banners + feature flags (public surface) ------------------

  async fetchActiveBanners(): Promise<Array<{
    id: string;
    message_fr: string;
    message_en: string;
    severity: 'info' | 'warning' | 'critical';
    starts_at: string | null;
    ends_at: string | null;
  }>> {
    const r = await this.client.get('/api/v1/banners/active');
    return Array.isArray(r.data) ? r.data : [];
  }

  async fetchMyFeatureFlags(): Promise<Record<string, boolean>> {
    const r = await this.client.get<Record<string, boolean>>('/api/v1/feature-flags/me');
    return r.data ?? {};
  }

  private clearAuthState(): void {
    this.token = null;
    localStorage.removeItem('auth_status');
    localStorage.removeItem('currentOrganisationId');
    csrfTokenManager.clearToken();
    oauthStateManager.clearAllStates();
  }

  async logout(): Promise<void> {
    try {
      if (this.token) {
        await this.client.post('/api/v1/auth/logout');
      }
    } catch {
      // Ignorer les erreurs (token/cookie peut être déjà expiré)
    } finally {
      this.clearAuthState();
    }
  }

  async register(userData: UserCreate): Promise<void> {
    Logger.log('[ApiService] Envoi requête register');
    const response = await this.client.post('/api/v1/auth/register', userData);
    Logger.log('[ApiService] Register response received', response.status);
  }

  async verifyAccount(verificationData: UserVerification): Promise<VerificationResponse> {
    const response: AxiosResponse<VerificationResponse> = await this.client.post('/api/v1/auth/verify-account', verificationData);
    return response.data;
  }

  async resendVerification(email: string): Promise<void> {
    await this.client.post('/api/v1/auth/resend-verification', { email });
  }

  async requestPasswordReset(resetData: PasswordResetRequest): Promise<PasswordResetResponse> {
    const response: AxiosResponse<PasswordResetResponse> = await this.client.post('/api/v1/auth/request-password-reset', resetData);
    return response.data;
  }

  async resetPassword(resetData: PasswordResetConfirm): Promise<void> {
    await this.client.post('/api/v1/auth/reset-password', resetData);
  }

  /**
   * Recueille l'acceptation des conditions pour le compte connecte.
   * Rend la fiche a jour : l'appelant n'a pas a refaire un GET pour la relire.
   */
  async accepterConditions(version: string): Promise<User> {
    const response: AxiosResponse<User> = await this.client.post(
      '/api/v1/users/me/conditions', { conditions_version: version });
    return response.data;
  }

  async getCurrentUser(): Promise<User> {
    const response: AxiosResponse<User> = await this.client.get('/api/v1/users/me');
    return response.data;
  }

  async updateProfile(profileData: Partial<User>): Promise<User> {
    const response: AxiosResponse<User> = await this.client.put('/api/v1/users/me', profileData);
    return response.data;
  }

  // Projets
  async getProjects(organisationId?: string, regionId?: string): Promise<Project[]> {
    const params: Record<string, string> = {};
    if (organisationId) params.organisation_id = organisationId;
    if (regionId) params.region_id = regionId;
    const response: AxiosResponse<Project[]> = await this.client.get('/api/v1/projects/', { params });
    return response.data;
  }

  async getProject(id: string): Promise<Project> {
    const response: AxiosResponse<Project> = await this.client.get(`/api/v1/projects/${id}`);
    return response.data;
  }

  async createProject(project: ProjectCreate & { organisation_id: string }): Promise<Project> {
    const { organisation_id, ...projectData } = project;
    const response: AxiosResponse<Project> = await this.client.post(`/api/v1/projects/?organisation_id=${organisation_id}`, projectData);
    return response.data;
  }

  async updateProject(id: string, project: Partial<Project>): Promise<Project> {
    const response: AxiosResponse<Project> = await this.client.put(`/api/v1/projects/${id}`, project);
    return response.data;
  }

  async deleteProject(id: string): Promise<void> {
    await this.client.delete(`/api/v1/projects/${id}`);
  }

  // Environnements
  async getProjectEnvironments(projectId: string): Promise<Environment[]> {
    const response: AxiosResponse<Environment[]> = await this.client.get(`/api/v1/environments/${projectId}/environments`);
    return response.data;
  }

  async getEnvironment(id: string): Promise<Environment> {
    const response: AxiosResponse<Environment> = await this.client.get(`/api/v1/environments/${id}`);
    return response.data;
  }

  async createEnvironment(projectId: string, environment: EnvironmentCreate): Promise<Environment> {
    const response: AxiosResponse<Environment> = await this.client.post(`/api/v1/environments/${projectId}/environments`, environment);
    return response.data;
  }

  async getEnvironmentStatus(environmentId: string): Promise<EnvironmentStatus> {
    const response: AxiosResponse<EnvironmentStatus> = await this.client.get(`/api/v1/environments/${environmentId}/status`);
    return response.data;
  }

  async getEnvironmentConfig(environmentId: string): Promise<EnvironmentConfig> {
    const response = await this.client.get<EnvironmentConfig>(`/api/v1/environments/${environmentId}/config`);
    return response.data;
  }

  async updateEnvironment(environmentId: string, environment: EnvironmentUpdate): Promise<Environment> {
    const response: AxiosResponse<Environment> = await this.client.put(`/api/v1/environments/${environmentId}`, environment);
    return response.data;
  }

  async deleteEnvironment(environmentId: string): Promise<void> {
    await this.client.delete(`/api/v1/environments/${environmentId}`);
  }

  // Déploiements
  async getEnvironmentDeployments(environmentId: string): Promise<Deployment[]> {
    const response: AxiosResponse<Deployment[]> = await this.client.get(`/api/v1/deployments/${environmentId}/deployments`);
    return response.data;
  }

  async getDeployment(deploymentId: string): Promise<Deployment> {
    const response: AxiosResponse<Deployment> = await this.client.get(`/api/v1/deployments/deployment/${deploymentId}`);
    return response.data;
  }

  async deployCodeApplication(environmentId: string, deployRequest: CodeApplicationCreate): Promise<Deployment> {
    const response: AxiosResponse<Deployment> = await this.client.post(`/api/v1/deployments/${environmentId}/deploy/code`, deployRequest);
    return response.data;
  }

  async deployERPApplication(environmentId: string, deployRequest: ERPApplicationCreate): Promise<Deployment> {
    const response: AxiosResponse<Deployment> = await this.client.post(`/api/v1/deployments/${environmentId}/deploy/erp`, deployRequest);
    return response.data;
  }

  async deployManagedService(environmentId: string, deployRequest: {
    nom: string;
    cpu: number;
    ram: number;
    storage?: number;
    replicas?: number;
    type_application: string;
    // Keycloak n'a pas de mot de passe root : son admin est genere par l'API et
    // affiche une seule fois. D'ou l'optionalite.
    root_password?: string;
    database?: string;
    username?: string;
    password?: string;
    version?: string;
    enable_backups?: boolean;
    additional_databases?: Array<{name: string; user?: string; password?: string; owner?: string}>;
    extensions?: string[];   // PostgreSQL : postgis, pgvector (nubi-postgres >= 1.3.0)
    // Keycloak : PostgreSQL manage qui hebergera sa base (obligatoire), et
    // domaine personnalise servi par le meme Ingress (l'issuer OIDC reste le
    // hostname auto-genere).
    database_deployment_id?: string;
    custom_domain?: string;
  }): Promise<Deployment> {
    const response: AxiosResponse<Deployment> = await this.client.post(`/api/v1/deployments/${environmentId}/deploy/managed-service`, deployRequest);
    return response.data;
  }

  /** PostgreSQL manages de cet environnement pouvant heberger un Keycloak.
   *  Renvoie la liste COMPLETE avec un drapeau et un motif : un PostgreSQL qui
   *  disparait du selecteur sans explication est un bug du point de vue de
   *  l'utilisateur, qui le voit dans sa console a cote. */
  async getKeycloakEligibleDatabases(environmentId: string): Promise<KeycloakEligibleDatabases> {
    const response: AxiosResponse<KeycloakEligibleDatabases> = await this.client.get(
      `/api/v1/deployments/${environmentId}/keycloak/eligible-databases`
    );
    return response.data;
  }

  /** Active ou retire l'acces temporaire pose devant une application a
   *  installation manuelle. `enabled=true` renvoie un mot de passe NEUF,
   *  restitue une seule fois : l'ancien n'est stocke en clair nulle part. */
  async setSetupProtection(deploymentId: string, enabled: boolean): Promise<{
    deployment_id: string;
    protection_enabled: boolean;
    protection_credentials?: { username: string; password: string };
  }> {
    const response = await this.client.patch(
      `/api/v1/deployments/deployment/${deploymentId}/protection`,
      null,
      { params: { enabled } }
    );
    return response.data;
  }

  async syncAdditionalDatabases(deploymentId: string): Promise<{status: string; logs: string; job_name: string | null}> {
    const response = await this.client.post<{status: string; logs: string; job_name: string | null}>(`/api/v1/deployments/deployment/${deploymentId}/sync-databases`);
    return response.data;
  }

  async syncDeployment(deploymentId: string): Promise<SyncDeploymentResponse> {
    const response = await this.client.post<SyncDeploymentResponse>(`/api/v1/deployments/deployment/${deploymentId}/sync`);
    return response.data;
  }

  async restartDeployment(deploymentId: string, resourceName?: string): Promise<RestartDeploymentResponse> {
    const url = `/api/v1/deployments/deployment/${deploymentId}/restart`;
    const params = resourceName ? { resource_name: resourceName } : {};
    const response = await this.client.post<RestartDeploymentResponse>(url, null, { params });
    return response.data;
  }

  async checkDeploymentHealth(deploymentId: string): Promise<{
    available: boolean;
    status_code: number | null;
    response_time_ms: number;
    application_url: string;
    error?: string;
    checked_at: string;
  }> {
    const response = await this.client.get(`/api/v1/deployments/deployment/${deploymentId}/health`);
    return response.data;
  }

  async getDeploymentDetails(deploymentId: string): Promise<DeploymentDetail> {
    const response: AxiosResponse<DeploymentDetail> = await this.client.get(`/api/v1/deployments/deployment/${deploymentId}`);
    return response.data;
  }

  async updateDeployment(deploymentId: string, updateData: DeploymentUpdate): Promise<Deployment> {
    const response: AxiosResponse<Deployment> = await this.client.put(`/api/v1/deployments/deployment/${deploymentId}`, updateData);
    return response.data;
  }

  async getDeploymentAvailableUpdates(deploymentId: string): Promise<AvailableUpdatesResponse> {
    const response = await this.client.get<AvailableUpdatesResponse>(`/api/v1/deployments/deployment/${deploymentId}/available-updates`);
    return response.data;
  }

  /**
   * Supprime un deploiement.
   *
   * finalBackup : sur un PostgreSQL manage, l'API prend une sauvegarde et
   * attend son aboutissement avant de detruire quoi que ce soit. Un echec ou
   * un depassement renvoie 409 -- l'appelant doit proposer de reessayer, ou de
   * passer false pour supprimer sans filet.
   */
  async deleteDeployment(deploymentId: string, finalBackup: boolean = true): Promise<void> {
    await this.client.delete(`/api/v1/deployments/deployment/${deploymentId}`, {
      params: { final_backup: finalBackup },
    });
  }

  /**
   * Pose ou leve la protection contre la suppression.
   *
   * Geste volontairement distinct de la suppression : c'est ce qui rend la
   * protection utilisable plutot qu'une impasse.
   */
  async setDeletionProtection(deploymentId: string, enabled: boolean): Promise<void> {
    await this.client.patch(
      `/api/v1/deployments/deployment/${deploymentId}/deletion-protection`,
      null,
      { params: { enabled } },
    );
  }

  async getDeploymentMetrics(deploymentId: string): Promise<DeploymentMetrics> {
    const response = await this.client.get<DeploymentMetrics>(`/api/v1/monitoring/applications/${deploymentId}/metrics`);
    return response.data;
  }

  // Buildpacks
  async getBuildpacks(): Promise<BuildpackInfo[]> {
    const response = await this.client.get<BuildpackInfo[]>('/api/v1/deployments/buildpacks');
    return response.data;
  }

  // Monitoring
  async getProjectMetrics(projectId: string): Promise<AppMetrics> {
    const response: AxiosResponse<AppMetrics> = await this.client.get(`/api/v1/monitoring/projects/${projectId}/metrics`);
    return response.data;
  }

  // Built Images
  async getBuiltImages(params?: {
    limit?: number;
    offset?: number;
    status_filter?: string;
    project_id?: string;
    successful_only?: boolean;
  }): Promise<BuiltImagesResponse> {
    const queryParams = new URLSearchParams();
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.offset) queryParams.append('offset', params.offset.toString());
    if (params?.status_filter) queryParams.append('status_filter', params.status_filter);
    if (params?.project_id) queryParams.append('project_id', params.project_id);
    if (params?.successful_only) queryParams.append('successful_only', 'true');

    const url = `/api/v1/builds/images${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    const response: AxiosResponse<BuiltImagesResponse> = await this.client.get(url);
    return response.data;
  }

  async deleteBuiltImage(buildId: string): Promise<void> {
    await this.client.delete(`/api/v1/builds/images/${buildId}`);
  }

  // Gestion des membres de projet
  async getProjectMembers(projectId: string): Promise<ProjectMember[]> {
    const response: AxiosResponse<ProjectMember[]> = await this.client.get(`/api/v1/projects/${projectId}/members`);
    return response.data;
  }

  async getInvitableMembers(projectId: string): Promise<InvitableMember[]> {
    const response: AxiosResponse<InvitableMember[]> = await this.client.get(`/api/v1/projects/${projectId}/invitable-members`);
    return response.data;
  }

  async addProjectMember(projectId: string, member: ProjectMemberCreate): Promise<AddMemberResponse> {
    const response: AxiosResponse<AddMemberResponse> = await this.client.post(`/api/v1/projects/${projectId}/members`, member);
    return response.data;
  }

  async updateProjectMember(projectId: string, memberId: string, memberUpdate: ProjectMemberUpdate): Promise<ProjectMember> {
    const response: AxiosResponse<ProjectMember> = await this.client.put(`/api/v1/projects/${projectId}/members/${memberId}`, memberUpdate);
    return response.data;
  }

  async removeProjectMember(projectId: string, memberId: string): Promise<void> {
    await this.client.delete(`/api/v1/projects/${projectId}/members/${memberId}`);
  }

  // Gestion des invitations
  async getInvitationInfo(token: string): Promise<InvitationInfo> {
    const response: AxiosResponse<InvitationInfo> = await this.client.get(`/api/v1/invitations/${token}`);
    return response.data;
  }

  async acceptInvitation(acceptData: InvitationAcceptRequest): Promise<InvitationAcceptResponse> {
    const response: AxiosResponse<InvitationAcceptResponse> = await this.client.post('/api/v1/invitations/accept', acceptData);
    return response.data;
  }

  async getPendingInvitations(): Promise<Invitation[]> {
    const response: AxiosResponse<Invitation[]> = await this.client.get('/api/v1/invitations/pending');
    return response.data;
  }

  // ============================================================================
  // PRICING ENDPOINTS
  // ============================================================================

  // Calculer le prix pour des ressources données
  async calculatePricing(request: PricingCalculationRequest, includeComparison: boolean = true): Promise<PricingCalculationResponse> {
    const response: AxiosResponse<PricingCalculationResponse> = await this.client.post(
      `/api/v1/pricing/calculate?include_comparison=${includeComparison}`,
      request
    );
    return response.data;
  }

  // Obtenir tous les presets disponibles
  async getAllPresets(): Promise<AllApplicationPresetsResponse> {
    const response: AxiosResponse<AllApplicationPresetsResponse> = await this.client.get('/api/v1/pricing/presets');
    return response.data;
  }

  // Obtenir une recommandation de preset
  async getPresetRecommendation(request: PresetRecommendationRequest): Promise<RecommendedPresetResponse> {
    const response: AxiosResponse<RecommendedPresetResponse> = await this.client.post('/api/v1/pricing/recommend', request);
    return response.data;
  }

  // Obtenir l'utilisation et les coûts de l'utilisateur connecté (nécessite authentification)
  async getMyUsage(): Promise<UserPricingUsageResponse> {
    const response: AxiosResponse<UserPricingUsageResponse> = await this.client.get('/api/v1/pricing/my-usage');
    return response.data;
  }

  // Estimer le coût d'un projet (nécessite authentification)
  async estimateProjectCost(
    cpu: number,
    ram: number,
    storage: number,
    appType?: 'wordpress' | 'odoo'
  ): Promise<ProjectEstimationResponse> {
    const params = new URLSearchParams({
      cpu: cpu.toString(),
      ram: ram.toString(),
      storage: storage.toString()
    });

    if (appType) {
      params.append('app_type', appType);
    }

    const response: AxiosResponse<ProjectEstimationResponse> = await this.client.get(
      `/api/v1/pricing/estimate-project?${params.toString()}`
    );
    return response.data;
  }

  // Obtenir la comparaison avec les concurrents pour un prix donné
  async getCompetitorComparison(monthlyPrice: number): Promise<PricingComparisonResponse> {
    const response: AxiosResponse<PricingComparisonResponse> = await this.client.get(
      `/api/v1/pricing/comparison?monthly_price=${monthlyPrice}`
    );
    return response.data;
  }

  // Suppression de compte utilisateur avec transfert d'assets
  // Workflow en 2 étapes:
  // 1. confirmTransfer=false → Preview des assets à transférer
  // 2. confirmTransfer=true → Exécute le transfert et supprime le compte
  async deleteAccount(confirmTransfer: boolean = false): Promise<DeleteAccountResponse> {
    const response = await this.client.delete<DeleteAccountResponse>(
      `/api/v1/users/me?confirm_transfer=${confirmTransfer}`
    );

    // Si c'est une suppression confirmée (pas une preview), déconnecter l'utilisateur
    if (confirmTransfer && response.data && 'details' in response.data) {
      this.clearAuthState();
    }

    return response.data;
  }

  // Helper pour vérifier si la réponse est une preview
  isDeleteAccountPreview(response: DeleteAccountResponse): response is DeleteAccountPreviewResponse {
    return 'action' in response && response.action === 'preview';
  }

  // ============================================================================
  // OAUTH & REPOSITORY CONNECTION ENDPOINTS
  // ============================================================================

  // Initiate OAuth flow for a given provider
  async initiateOAuth(
    provider: 'github' | 'gitlab',
    monitoredBranch: string = 'main'
  ): Promise<{ auth_url: string; state: string }> {
    // Build the redirect URI pointing to the frontend oauth-callback page
    const redirectUri = `${window.location.origin}/oauth-callback`;

    const response: AxiosResponse<{ authorization_url: string; state: string }> =
      await this.client.post(`/api/v1/git/authorize`, {
        provider: provider.toLowerCase(),
        monitored_branch: monitoredBranch,
        redirect_uri: redirectUri
      });
    return {
      auth_url: response.data.authorization_url,
      state: response.data.state
    };
  }

  // Get available repositories after OAuth callback
  async getAvailableRepositories(state: string): Promise<{
    repositories: GitRepository[];
    user_info: GitUserInfo;
    provider: string;
  }> {
    // Use the new /session/{state} endpoint to retrieve cached OAuth data
    const response = await this.client.get(`/api/v1/git/session/${state}`);
    return response.data;
  }

  // Connect a repository after OAuth
  async connectRepository(
    repoId: string | number,
    provider: string,
    monitoredBranch: string = 'main'
  ): Promise<ConnectedRepository> {
    if (!repoId) {
      throw new Error('Repository ID is required to connect a repository');
    }
    const response = await this.client.post<ConnectedRepository>(
      `/api/v1/git/repositories/${repoId}/connect`,
      {
        provider: provider.toLowerCase(),
        provider_repo_id: String(repoId),
        monitored_branch: monitoredBranch
      }
    );
    return response.data;
  }

  // Quick Connect: Connecter un repository en 2 étapes via OAuth
  async quickConnectRepository(request: QuickConnectRequest): Promise<QuickConnectResponse> {
    const response: AxiosResponse<QuickConnectResponse> = await this.client.post(
      '/api/v1/oauth/quick-connect',
      request
    );
    return response.data;
  }

  // Récupérer la liste des repositories connectés
  async getConnectedRepositories(projectId?: string): Promise<ConnectedRepository[]> {
    const url = projectId
      ? `/api/v1/git/repositories/connected?project_id=${projectId}`
      : '/api/v1/git/repositories/connected';
    const response = await this.client.get<{ repositories: GitRepositoryResponse[] }>(url);

    // Map API response to ConnectedRepository interface
    return (response.data.repositories || []).map((repo: GitRepositoryResponse) => ({
      id: repo.id,
      user_id: '',
      oauth_provider_id: 0,
      project_id: null,
      provider_repo_id: '',
      repo_name: repo.repository_name,
      repo_owner: repo.repository_owner,
      repo_url: repo.repository_url,
      clone_url: repo.clone_url,
      default_branch: repo.default_branch,
      monitored_branch: repo.monitored_branch,
      webhook_enabled: repo.webhook_enabled,
      webhook_id: null,
      is_active: repo.is_active,
      created_at: repo.connected_at,
      updated_at: repo.connected_at,
      oauth_provider: {
        id: 0,
        name: repo.provider.toLowerCase(),
        display_name: repo.provider.charAt(0).toUpperCase() + repo.provider.slice(1).toLowerCase(),
        authorize_url: '',
        scopes: '',
        is_enabled: true
      }
    }));
  }

  // Déconnecter un repository
  async disconnectRepository(repoId: number): Promise<void> {
    await this.client.delete(`/api/v1/git/repositories/${repoId}`);
  }

  async toggleAutoBuild(repoId: number, enabled: boolean): Promise<{ id: number; webhook_enabled: boolean }> {
    const response = await this.client.patch(`/api/v1/git/repositories/${repoId}/auto-build?enabled=${enabled}`);
    return response.data;
  }

  // Get branches for a connected repository
  async getRepositoryBranches(repoId: number): Promise<{
    branches: Array<{ name: string; is_default: boolean }>;
    repository: string;
    default_branch: string;
  }> {
    const response = await this.client.get(
      `/api/v1/git/repositories/${repoId}/branches`
    );
    return response.data;
  }

  // ============================================================================
  // INTELLIGENT BUILD ENDPOINTS
  // ============================================================================

  // Lancer un build intelligent depuis un repository
  async launchIntelligentBuild(buildRequest: BuildRequest & { source_subpath?: string; project_id?: string }): Promise<BuildResponse> {
    // Extract app name from repository URL
    // e.g., https://github.com/owner/repo-name.git -> repo-name
    let appName = 'app';
    try {
      const urlMatch = buildRequest.repository_url.match(/\/([^\/]+?)(\.git)?$/);
      if (urlMatch && urlMatch[1]) {
        appName = urlMatch[1].replace('.git', '').toLowerCase().replace(/[^a-z0-9-]/g, '-');
      }
    } catch (e) {
      Logger.warn('Failed to extract app name from repository URL, using default:', e);
    }

    // Convert old BuildRequest to new IntelligentBuildRequest format
    const intelligentBuildRequest: Record<string, any> = {
      git_url: buildRequest.repository_url,
      app_name: appName,
      environment_variables: {},
      build_strategy: 'auto' as const,
      auto_deploy: false,
      commit_sha: buildRequest.branch || 'main',
      image_tag: buildRequest.image_tag || 'latest'
    };

    // Add source_subpath for monorepo support if provided
    if (buildRequest.source_subpath) {
      intelligentBuildRequest.source_subpath = buildRequest.source_subpath;
    }

    // Add project_id for RBAC scoping
    if (buildRequest.project_id) {
      intelligentBuildRequest.project_id = buildRequest.project_id;
    }

    const response: AxiosResponse<IntelligentBuildApiResponse> = await this.client.post(
      '/api/v1/builds/intelligent-build',
      intelligentBuildRequest
    );

    // Convert IntelligentBuildResponse to BuildResponse for compatibility
    return {
      success: response.data.success,
      build_id: response.data.build_id,
      message: response.data.message
    };
  }

  // Récupérer le statut d'un build
  async getBuildStatus(buildId: string): Promise<BuildStatus> {
    const response: AxiosResponse<BuildStatus> = await this.client.get(
      `/api/v1/builds/${buildId}/status`
    );
    return response.data;
  }

  async getBuildScanResults(buildId: string): Promise<{status: string; vulnerabilities: number; cves: any[]}> {
    const response = await this.client.get(`/api/v1/builds/${buildId}/scan`);
    return response.data;
  }

  // Récupérer les logs d'un build
  async getBuildLogs(buildId: string): Promise<BuildLogs> {
    const response: AxiosResponse<BuildLogs> = await this.client.get(
      `/api/v1/builds/${buildId}/logs`
    );
    return response.data;
  }

  // Supprimer/annuler un build
  async cancelBuild(buildId: string): Promise<{ success: boolean; message: string }> {
    const response: AxiosResponse<{ success: boolean; message: string }> = await this.client.post(
      `/api/v1/builds/${buildId}/cancel`
    );
    return response.data;
  }

  async deleteBuild(buildId: string): Promise<{ success: boolean; message: string }> {
    const response: AxiosResponse<{ success: boolean; message: string }> = await this.client.delete(
      `/api/v1/builds/${buildId}`
    );
    return response.data;
  }

  // Relancer un build
  async retryBuild(buildId: string): Promise<BuildResponse> {
    const response: AxiosResponse<IntelligentBuildApiResponse> = await this.client.post(
      `/api/v1/builds/${buildId}/retry`
    );

    // Convert IntelligentBuildResponse to BuildResponse for compatibility
    return {
      success: response.data.success,
      build_id: response.data.build_id,
      message: response.data.message
    };
  }

  // Récupérer tous les builds de l'utilisateur
  async getUserBuilds(projectId?: string): Promise<UserBuildsResponse> {
    const url = projectId ? `/api/v1/builds?project_id=${projectId}` : '/api/v1/builds';
    const response: AxiosResponse<UserBuildsResponse> = await this.client.get(url);
    return response.data;
  }

  // ============================================================================
  // IMAGE IMPORT ENDPOINTS (NubiImport)
  // ============================================================================

  async validateImage(request: ValidateImageRequest): Promise<ValidateImageResponse> {
    const response: AxiosResponse<ValidateImageResponse> = await this.client.post('/api/v1/imports/validate-image', request);
    return response.data;
  }

  async startImageImport(request: ImageImportRequest): Promise<ImageImportResponse> {
    const response: AxiosResponse<ImageImportResponse> = await this.client.post('/api/v1/imports', request);
    return response.data;
  }

  async getImportStatus(importId: string): Promise<ImportStatus> {
    const response: AxiosResponse<ImportStatus> = await this.client.get(`/api/v1/imports/${importId}/status`);
    return response.data;
  }

  async getImportLogs(importId: string): Promise<ImportLogs> {
    const response: AxiosResponse<ImportLogs> = await this.client.get(`/api/v1/imports/${importId}/logs`);
    return response.data;
  }

  async cancelImport(importId: string): Promise<{ success: boolean; message: string }> {
    const response: AxiosResponse<{ success: boolean; message: string }> = await this.client.post(`/api/v1/imports/${importId}/cancel`);
    return response.data;
  }

  async listImports(): Promise<{ imports: ImportStatus[]; total: number }> {
    const response: AxiosResponse<{ imports: ImportStatus[]; total: number }> = await this.client.get('/api/v1/imports');
    return response.data;
  }

  async storeCredentials(request: StoreCredentialsRequest): Promise<StoreCredentialsResponse> {
    const response: AxiosResponse<StoreCredentialsResponse> = await this.client.post('/api/v1/imports/credentials', request);
    return response.data;
  }

  async listCredentials(projectId?: string): Promise<CredentialListResponse> {
    const url = projectId
      ? `/api/v1/imports/credentials?project_id=${projectId}`
      : '/api/v1/imports/credentials';
    const response: AxiosResponse<CredentialListResponse> = await this.client.get(url);
    return response.data;
  }

  async deleteCredential(credentialId: string): Promise<{ success: boolean }> {
    const response: AxiosResponse<{ success: boolean }> = await this.client.delete(`/api/v1/imports/credentials/${credentialId}`);
    return response.data;
  }

  async revalidateCredential(credentialId: string): Promise<StoreCredentialsResponse> {
    const response: AxiosResponse<StoreCredentialsResponse> = await this.client.post(`/api/v1/imports/credentials/${credentialId}/revalidate`);
    return response.data;
  }

  // ============================================================================
  // ODOO MODULES ENDPOINTS
  // ============================================================================

  // Ajouter un module Odoo depuis un repository Git
  async addOdooModule(moduleData: OdooModuleAdd): Promise<OdooModule> {
    const response: AxiosResponse<OdooModule> = await this.client.post(
      '/api/v1/odoo/modules',
      moduleData
    );
    return response.data;
  }

  // Upload un module Odoo depuis un fichier ZIP
  async uploadOdooModuleZip(
    deploymentId: string,
    moduleName: string,
    file: File,
    autoInstall: boolean = false
  ): Promise<OdooModule> {
    const formData = new FormData();
    formData.append('deployment_id', deploymentId);
    formData.append('module_name', moduleName);
    formData.append('auto_install', String(autoInstall));
    formData.append('file', file);

    const response: AxiosResponse<OdooModule> = await this.client.post(
      '/api/v1/odoo/modules/upload',
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data;
  }

  // Lister les modules Odoo d'un déploiement
  async getOdooModules(deploymentId: string): Promise<OdooModuleListResponse> {
    const response: AxiosResponse<OdooModuleListResponse> = await this.client.get(
      `/api/v1/odoo/modules/${deploymentId}`
    );
    return response.data;
  }

  // Supprimer un module Odoo
  async deleteOdooModule(moduleId: number): Promise<void> {
    await this.client.delete(`/api/v1/odoo/modules/${moduleId}`);
  }

  // Recuperer les logs d'installation des modules Odoo
  async getOdooModuleLogs(
    deploymentId: string,
    since: string = '24h',
    tailLines: number = 200
  ): Promise<OdooModuleLogsResponse> {
    const response: AxiosResponse<OdooModuleLogsResponse> = await this.client.get(
      `/api/v1/odoo/modules/${deploymentId}/logs`,
      { params: { since, tail_lines: tailLines } }
    );
    return response.data;
  }

  // ============================================================================
  // INSTANCE TYPES ENDPOINTS
  // ============================================================================

  // Récupérer tous les instance types (presets)
  async getInstanceTypes(): Promise<ApiInstanceTypeResponse[]> {
    const response: AxiosResponse<ApiInstanceTypeResponse[]> = await this.client.get(
      '/api/v1/instance-types/?skip=0&limit=100&active_only=true'
    );
    return response.data;
  }

  // ============================================================================
  // REGION ENDPOINTS
  // ============================================================================

  async getRegions(organisationId?: string, includeInactive?: boolean): Promise<Region[]> {
    const queryParams = new URLSearchParams();
    if (organisationId) queryParams.set('organisation_id', organisationId);
    if (includeInactive) queryParams.set('include_inactive', 'true');
    const qs = queryParams.toString() ? `?${queryParams.toString()}` : '';
    const response: AxiosResponse<Region[]> = await this.client.get(`/api/v1/regions/${qs}`);
    return response.data;
  }

  async getRegion(regionId: string): Promise<Region> {
    const response: AxiosResponse<Region> = await this.client.get(`/api/v1/regions/${regionId}`);
    return response.data;
  }

  async createRegion(organisationId: string, data: RegionCreate): Promise<Region> {
    const response: AxiosResponse<Region> = await this.client.post(
      `/api/v1/regions/?organisation_id=${organisationId}`,
      data
    );
    return response.data;
  }

  async updateRegion(regionId: string, data: RegionUpdate): Promise<Region> {
    const response: AxiosResponse<Region> = await this.client.put(`/api/v1/regions/${regionId}`, data);
    return response.data;
  }

  async deleteRegion(regionId: string): Promise<void> {
    await this.client.delete(`/api/v1/regions/${regionId}`);
  }

  async reactivateRegion(regionId: string): Promise<Region> {
    const response: AxiosResponse<Region> = await this.client.post(`/api/v1/regions/${regionId}/reactivate`);
    return response.data;
  }

  async permanentlyDeleteRegion(regionId: string): Promise<void> {
    await this.client.delete(`/api/v1/regions/${regionId}/permanent`);
  }

  async generateInstallScript(regionId: string): Promise<GenerateInstallScriptResponse> {
    const response: AxiosResponse<GenerateInstallScriptResponse> = await this.client.post(
      `/api/v1/regions/${regionId}/generate-install-script`
    );
    return response.data;
  }

  async getRegionConnectionStatus(regionId: string): Promise<RegionConnectionInfo> {
    const response: AxiosResponse<RegionConnectionInfo> = await this.client.get(
      `/api/v1/regions/${regionId}/connection-status`
    );
    return response.data;
  }

  async disconnectRegion(regionId: string): Promise<void> {
    await this.client.post(`/api/v1/regions/${regionId}/disconnect`);
  }

  async manualCallback(regionId: string, data: RegionManualCallbackRequest): Promise<void> {
    await this.client.post(`/api/v1/regions/${regionId}/manual-callback`, data);
  }

  async reconnectRegion(regionId: string): Promise<GenerateInstallScriptResponse> {
    const response: AxiosResponse<GenerateInstallScriptResponse> = await this.client.post(
      `/api/v1/regions/${regionId}/reconnect`
    );
    return response.data;
  }

  // ============================================================================
  // BACKUP & RESTORE ENDPOINTS
  // ============================================================================

  // Configuration de backup
  async getBackupConfiguration(deploymentId: string): Promise<BackupConfiguration> {
    const response: AxiosResponse<BackupConfiguration> = await this.client.get(
      `/api/v1/deployments/${deploymentId}/backups/configuration`
    );
    return response.data;
  }

  async configureBackup(
    deploymentId: string,
    config: BackupConfigurationCreate
  ): Promise<BackupConfiguration> {
    const response: AxiosResponse<BackupConfiguration> = await this.client.post(
      `/api/v1/deployments/${deploymentId}/backups/configure`,
      config
    );
    return response.data;
  }

  async disableBackup(deploymentId: string): Promise<void> {
    await this.client.delete(
      `/api/v1/deployments/${deploymentId}/backups/configuration`
    );
  }

  // Gestion des backups
  async listBackups(
    deploymentId: string,
    page: number = 1,
    pageSize: number = 20
  ): Promise<BackupListResponse> {
    const response: AxiosResponse<BackupListResponse> = await this.client.get(
      `/api/v1/deployments/${deploymentId}/backups?page=${page}&page_size=${pageSize}`
    );
    return response.data;
  }

  async getBackup(deploymentId: string, backupId: string): Promise<Backup> {
    const response: AxiosResponse<Backup> = await this.client.get(
      `/api/v1/deployments/${deploymentId}/backups/${backupId}`
    );
    return response.data;
  }

  async createOnDemandBackup(deploymentId: string): Promise<CreateOnDemandBackupResponse> {
    const response: AxiosResponse<CreateOnDemandBackupResponse> = await this.client.post(
      `/api/v1/deployments/${deploymentId}/backups/ondemand`
    );
    return response.data;
  }

  async deleteBackup(deploymentId: string, backupId: string): Promise<{ message: string; backup_id: string }> {
    const response: AxiosResponse<{ message: string; backup_id: string }> = await this.client.delete(
      `/api/v1/deployments/${deploymentId}/backups/${backupId}`
    );
    return response.data;
  }

  // Opérations de restauration (FULL restore uniquement)
  async initiateRestore(
    deploymentId: string,
    backupId: string
  ): Promise<RestoreOperation> {
    const response: AxiosResponse<RestoreOperation> = await this.client.post(
      `/api/v1/deployments/${deploymentId}/backups/${backupId}/restore`,
      {} // Empty body - FULL restore par défaut
    );
    return response.data;
  }

  async getRestoreStatus(
    deploymentId: string,
    restoreId: string
  ): Promise<RestoreOperation> {
    const response: AxiosResponse<RestoreOperation> = await this.client.get(
      `/api/v1/deployments/${deploymentId}/restores/${restoreId}`
    );
    return response.data;
  }

  async validateRestore(
    deploymentId: string,
    restoreId: string,
    confirm: boolean
  ): Promise<RestoreOperation> {
    const response: AxiosResponse<RestoreOperation> = await this.client.post(
      `/api/v1/deployments/${deploymentId}/restores/${restoreId}/validate`,
      { confirm }
    );
    return response.data;
  }

  async cancelRestore(deploymentId: string, restoreId: string): Promise<void> {
    await this.client.post(
      `/api/v1/deployments/${deploymentId}/restores/${restoreId}/cancel`
    );
  }

  async listRestores(deploymentId: string): Promise<RestoreListResponse> {
    const response: AxiosResponse<RestoreListResponse> = await this.client.get(
      `/api/v1/deployments/${deploymentId}/restores`
    );
    return response.data;
  }

  async deleteRestore(deploymentId: string, restoreId: string): Promise<{ message: string; restore_id: string }> {
    const response: AxiosResponse<{ message: string; restore_id: string }> = await this.client.delete(
      `/api/v1/deployments/${deploymentId}/restores/${restoreId}`
    );
    return response.data;
  }

  async getAllUserBackups(projectId?: string): Promise<AllUserBackupsResponse> {
    const url = projectId
      ? `/api/v1/backups/all?project_id=${projectId}`
      : '/api/v1/backups/all';
    const response: AxiosResponse<AllUserBackupsResponse> = await this.client.get(url);
    return response.data;
  }

// =====================================================
  // ORGANISATION ENDPOINTS
  // =====================================================

  // Organisation CRUD
  async getOrganisations(): Promise<Organisation[]> {
    const response: AxiosResponse<Organisation[]> = await this.client.get('/api/v1/organisations/');
    return response.data;
  }

  async getOrganisation(id: string): Promise<Organisation> {
    const response: AxiosResponse<Organisation> = await this.client.get(`/api/v1/organisations/${id}`);
    return response.data;
  }

  async createOrganisation(data: OrganisationCreate): Promise<Organisation> {
    const response: AxiosResponse<Organisation> = await this.client.post('/api/v1/organisations/', data);
    return response.data;
  }

  async updateOrganisation(id: string, data: OrganisationUpdate): Promise<Organisation> {
    const response: AxiosResponse<Organisation> = await this.client.put(`/api/v1/organisations/${id}`, data);
    return response.data;
  }

  async deleteOrganisation(id: string, confirmData: OrganisationDeleteRequest): Promise<void> {
    await this.client.delete(`/api/v1/organisations/${id}`, { data: confirmData });
  }

  async getDeletedOrganisations(): Promise<Organisation[]> {
    const response: AxiosResponse<Organisation[]> = await this.client.get('/api/v1/organisations/deleted');
    return response.data;
  }

  async restoreOrganisation(id: string): Promise<OrganisationRestoreResponse> {
    const response: AxiosResponse<OrganisationRestoreResponse> = await this.client.post(`/api/v1/organisations/${id}/restore`);
    return response.data;
  }

  // Members Management
  async getOrganisationMembers(organisationId: string): Promise<OrganisationMember[]> {
    const response: AxiosResponse<OrganisationMember[]> = await this.client.get(`/api/v1/organisations/${organisationId}/members`);
    return response.data;
  }

  async inviteOrganisationMember(organisationId: string, data: OrganisationMemberCreate): Promise<InviteMemberResponse> {
    const response: AxiosResponse<InviteMemberResponse> = await this.client.post(`/api/v1/organisations/${organisationId}/members`, data);
    return response.data;
  }

  async updateOrganisationMember(organisationId: string, memberId: string, data: OrganisationMemberUpdate): Promise<OrganisationMemberUpdateResponse> {
    const response = await this.client.put<OrganisationMemberUpdateResponse>(`/api/v1/organisations/${organisationId}/members/${memberId}`, data);
    return response.data;
  }

  async removeOrganisationMember(
    organisationId: string,
    userId: string,
    confirmTransfer: boolean = false
  ): Promise<RemoveMemberResponse> {
    const response = await this.client.delete<RemoveMemberResponse>(
      `/api/v1/organisations/${organisationId}/members/${userId}?confirm_transfer=${confirmTransfer}`
    );
    return response.data;
  }

  // Helper pour vérifier si la réponse est une preview de suppression de membre
  isRemoveMemberPreview(response: RemoveMemberResponse): response is RemoveMemberPreviewResponse {
    return 'action' in response && response.action === 'preview';
  }

  // Groupes d'une organisation (plan B) : sujets d'autorisation sur les projets
  async getOrganisationGroups(organisationId: string): Promise<Group[]> {
    const response = await this.client.get<Group[]>(`/api/v1/organisations/${organisationId}/groups`);
    return response.data;
  }

  async createOrganisationGroup(organisationId: string, data: GroupCreate): Promise<Group> {
    const response = await this.client.post<Group>(`/api/v1/organisations/${organisationId}/groups`, data);
    return response.data;
  }

  async getGroup(groupId: string): Promise<Group> {
    const response = await this.client.get<Group>(`/api/v1/groups/${groupId}`);
    return response.data;
  }

  async updateGroup(groupId: string, data: GroupUpdate): Promise<Group> {
    const response = await this.client.put<Group>(`/api/v1/groups/${groupId}`, data);
    return response.data;
  }

  async deleteGroup(groupId: string): Promise<void> {
    await this.client.delete(`/api/v1/groups/${groupId}`);
  }

  async getGroupMembers(groupId: string): Promise<GroupMember[]> {
    const response = await this.client.get<GroupMember[]>(`/api/v1/groups/${groupId}/members`);
    return response.data;
  }

  async addGroupMember(groupId: string, userId: string): Promise<GroupMember> {
    const response = await this.client.post<GroupMember>(`/api/v1/groups/${groupId}/members/${userId}`);
    return response.data;
  }

  async removeGroupMember(groupId: string, userId: string): Promise<void> {
    await this.client.delete(`/api/v1/groups/${groupId}/members/${userId}`);
  }

  async getGroupProjects(groupId: string): Promise<GroupProject[]> {
    const response = await this.client.get<GroupProject[]>(`/api/v1/groups/${groupId}/projects`);
    return response.data;
  }

  async grantGroupProject(groupId: string, projectId: string, role: ProjectRole): Promise<GroupProject> {
    const response = await this.client.post<GroupProject>(`/api/v1/groups/${groupId}/projects/${projectId}`, { role_projet: role });
    return response.data;
  }

  async updateGroupProject(groupId: string, projectId: string, role: ProjectRole): Promise<GroupProject> {
    const response = await this.client.put<GroupProject>(`/api/v1/groups/${groupId}/projects/${projectId}`, { role_projet: role });
    return response.data;
  }

  async revokeGroupProject(groupId: string, projectId: string): Promise<void> {
    await this.client.delete(`/api/v1/groups/${groupId}/projects/${projectId}`);
  }

  // Vue projet : qui a acces et pourquoi, et les groupes accordes
  async getProjectAccess(projectId: string): Promise<ProjectEffectiveMember[]> {
    const response = await this.client.get<ProjectEffectiveMember[]>(`/api/v1/projects/${projectId}/access`);
    return response.data;
  }

  async getProjectGroups(projectId: string): Promise<ProjectGroup[]> {
    const response = await this.client.get<ProjectGroup[]>(`/api/v1/projects/${projectId}/groups`);
    return response.data;
  }

  // Quota Management
  async getOrganisationQuotaUsage(organisationId: string): Promise<OrganisationQuotaUsage> {
    const response: AxiosResponse<OrganisationQuotaUsage> = await this.client.get(`/api/v1/organisations/${organisationId}/quota-usage`);
    return response.data;
  }

  // Plan Management
  async getAvailablePlans(): Promise<AvailablePlansResponse> {
    const response: AxiosResponse<AvailablePlansResponse> = await this.client.get('/api/v1/organisations/plans/available');
    return response.data;
  }

  async changeOrganisationPlan(organisationId: string, data: PlanChangeRequest): Promise<Organisation> {
    const response: AxiosResponse<Organisation> = await this.client.post(`/api/v1/organisations/${organisationId}/change-plan`, data);
    return response.data;
  }

  async requestEnterprisePlan(organisationId: string, data: EnterpriseRequestData): Promise<EnterpriseRequestResponse> {
    const response: AxiosResponse<EnterpriseRequestResponse> = await this.client.post(`/api/v1/organisations/${organisationId}/request-enterprise`, data);
    return response.data;
  }

  // =====================================================
  // ORGANISATION INVITATIONS ENDPOINTS
  // =====================================================

  async getPendingOrganisationInvitations(): Promise<OrganisationInvitation[]> {
    const response: AxiosResponse<OrganisationInvitation[]> = await this.client.get('/api/v1/organisation-invitations/pending');
    return response.data;
  }

  async getOrganisationInvitationInfo(token: string): Promise<OrganisationInvitationInfo> {
    const response: AxiosResponse<OrganisationInvitationInfo> = await this.client.get(`/api/v1/organisation-invitations/${token}`);
    return response.data;
  }

  async acceptOrganisationInvitation(data: OrganisationInvitationAcceptRequest): Promise<OrganisationInvitationAcceptResponse> {
    const response: AxiosResponse<OrganisationInvitationAcceptResponse> = await this.client.post('/api/v1/organisation-invitations/accept', data);
    return response.data;
  }

  // ============================================
  // Prometheus Metrics
  // ============================================

  async checkPrometheusHealth(): Promise<PrometheusHealthResponse> {
    const response: AxiosResponse<PrometheusHealthResponse> = await this.client.get(
      `/api/v1/prometheus-metrics/health`
    );
    return response.data;
  }

  async getDeploymentCPUMetrics(
    deploymentId: string,
    namespace: string,
    tenantId: string,
    startTime: string = 'now-1h',
    endTime: string = 'now',
    step: string = '15s'
  ): Promise<CPUMetricsResponse> {
    const params = new URLSearchParams({
      namespace,
      tenant_id: tenantId,
      start_time: startTime,
      end_time: endTime,
      step
    });
    const response: AxiosResponse<CPUMetricsResponse> = await this.client.get(
      `/api/v1/prometheus-metrics/deployments/${deploymentId}/cpu?${params.toString()}`
    );
    return response.data;
  }

  async getDeploymentMemoryMetrics(
    deploymentId: string,
    namespace: string,
    tenantId: string,
    startTime: string = 'now-1h',
    endTime: string = 'now',
    step: string = '15s'
  ): Promise<MemoryMetricsResponse> {
    const params = new URLSearchParams({
      namespace,
      tenant_id: tenantId,
      start_time: startTime,
      end_time: endTime,
      step
    });
    const response: AxiosResponse<MemoryMetricsResponse> = await this.client.get(
      `/api/v1/prometheus-metrics/deployments/${deploymentId}/memory?${params.toString()}`
    );
    return response.data;
  }

  async getDeploymentNetworkMetrics(
    deploymentId: string,
    namespace: string,
    tenantId: string,
    startTime: string = 'now-1h',
    endTime: string = 'now',
    step: string = '15s'
  ): Promise<NetworkMetricsResponse> {
    const params = new URLSearchParams({
      namespace,
      tenant_id: tenantId,
      start_time: startTime,
      end_time: endTime,
      step
    });
    const response: AxiosResponse<NetworkMetricsResponse> = await this.client.get(
      `/api/v1/prometheus-metrics/deployments/${deploymentId}/network?${params.toString()}`
    );
    return response.data;
  }

  async getDeploymentStorageMetrics(
    deploymentId: string,
    namespace: string,
    tenantId: string,
    startTime: string = 'now-1h',
    endTime: string = 'now',
    step: string = '15s'
  ): Promise<StorageMetricsResponse> {
    const params = new URLSearchParams({
      namespace,
      tenant_id: tenantId,
      start_time: startTime,
      end_time: endTime,
      step
    });
    const response: AxiosResponse<StorageMetricsResponse> = await this.client.get(
      `/api/v1/prometheus-metrics/deployments/${deploymentId}/storage?${params.toString()}`
    );
    return response.data;
  }

  async getAllDeploymentMetrics(
    deploymentId: string,
    namespace: string,
    tenantId: string,
    startTime: string = 'now-1h',
    endTime: string = 'now',
    step: string = '15s'
  ): Promise<AllMetricsResponse> {
    const params = new URLSearchParams({
      namespace,
      tenant_id: tenantId,
      start_time: startTime,
      end_time: endTime,
      step
    });
    const response: AxiosResponse<AllMetricsResponse> = await this.client.get(
      `/api/v1/prometheus-metrics/deployments/${deploymentId}/all?${params.toString()}`
    );
    return response.data;
  }

  // ============================================
  // Migration & Job Logs
  // ============================================

  async getMigrationLogs(
    deploymentId: string,
    since: string = '24h',
    tailLines: number = 500
  ): Promise<MigrationLogsResponse> {
    const params = new URLSearchParams({
      since,
      tail_lines: tailLines.toString()
    });
    const response: AxiosResponse<MigrationLogsResponse> = await this.client.get(
      `/api/v1/logs/applications/${deploymentId}/migration/logs?${params.toString()}`
    );
    return response.data;
  }

  async getApplicationJobs(deploymentId: string): Promise<ApplicationJobsResponse> {
    const response: AxiosResponse<ApplicationJobsResponse> = await this.client.get(
      `/api/v1/logs/applications/${deploymentId}/jobs`
    );
    return response.data;
  }

  async getJobLogs(
    deploymentId: string,
    jobName: string,
    since: string = '1h',
    tailLines: number = 500
  ): Promise<JobLogsResponse> {
    const params = new URLSearchParams({
      since,
      tail_lines: tailLines.toString()
    });
    const response: AxiosResponse<JobLogsResponse> = await this.client.get(
      `/api/v1/logs/applications/${deploymentId}/jobs/${encodeURIComponent(jobName)}/logs?${params.toString()}`
    );
    return response.data;
  }

  // ============================================
  // Application logs (historique + search + download + pods list)
  // Backend Loki (cf. docs/logs-api-refactor.md côté API)
  // ============================================

  async getApplicationLogs(
    deploymentId: string,
    options: {
      container?: string;
      since?: string;
      tailLines?: number;
    } = {}
  ): Promise<{
    application: string;
    namespace: string;
    logs: string;
    pods: Array<{ name: string; health: string }>;
    container: string | null;
    since: string;
    tail_lines: number;
    timestamp: string;
  }> {
    const params = new URLSearchParams({
      since: options.since ?? '1h',
      tail_lines: (options.tailLines ?? 100).toString(),
    });
    if (options.container) params.set('container', options.container);
    const response = await this.client.get(
      `/api/v1/logs/applications/${deploymentId}/logs?${params.toString()}`
    );
    return response.data;
  }

  async searchApplicationLogs(
    deploymentId: string,
    query: string,
    options: { since?: string; maxLines?: number } = {}
  ): Promise<{
    application: string;
    namespace: string;
    query: string;
    matches: Array<{ line_number: number; content: string; timestamp_extracted: string }>;
    total_matches: number;
    since: string;
    timestamp: string;
  }> {
    const params = new URLSearchParams({
      query,
      since: options.since ?? '1h',
      max_lines: (options.maxLines ?? 500).toString(),
    });
    const response = await this.client.get(
      `/api/v1/logs/applications/${deploymentId}/logs/search?${params.toString()}`
    );
    return response.data;
  }

  async downloadApplicationLogs(deploymentId: string, since: string = '24h'): Promise<Blob> {
    const params = new URLSearchParams({ since });
    const response = await this.client.get(
      `/api/v1/logs/applications/${deploymentId}/logs/download?${params.toString()}`,
      { responseType: 'blob' }
    );
    return response.data;
  }

  async getApplicationPods(
    deploymentId: string
  ): Promise<{ application: string; namespace: string; pods: any[]; pods_count: number }> {
    const response = await this.client.get(
      `/api/v1/logs/applications/${deploymentId}/pods`
    );
    return response.data;
  }

  // ============================================
  // Build Configuration (Webhook Auto-Build)
  // ============================================

  async listBuildConfigurations(repositoryId: number): Promise<BuildConfigurationListResponse> {
    const response: AxiosResponse<BuildConfigurationListResponse> = await this.client.get(
      `/api/v1/repositories/${repositoryId}/build-configs`
    );
    return response.data;
  }

  async getBuildConfiguration(repositoryId: number, configId: number): Promise<BuildConfiguration> {
    const response: AxiosResponse<BuildConfiguration> = await this.client.get(
      `/api/v1/repositories/${repositoryId}/build-configs/${configId}`
    );
    return response.data;
  }

  async createBuildConfiguration(
    repositoryId: number,
    config: BuildConfigurationCreate
  ): Promise<BuildConfiguration> {
    const response: AxiosResponse<BuildConfiguration> = await this.client.post(
      `/api/v1/repositories/${repositoryId}/build-configs`,
      config
    );
    return response.data;
  }

  async updateBuildConfiguration(
    repositoryId: number,
    configId: number,
    config: BuildConfigurationUpdate
  ): Promise<BuildConfiguration> {
    const response: AxiosResponse<BuildConfiguration> = await this.client.put(
      `/api/v1/repositories/${repositoryId}/build-configs/${configId}`,
      config
    );
    return response.data;
  }

  async deleteBuildConfiguration(repositoryId: number, configId: number): Promise<void> {
    await this.client.delete(`/api/v1/repositories/${repositoryId}/build-configs/${configId}`);
  }

  async triggerBuildFromConfig(
    repositoryId: number,
    configId: number,
    request?: BuildTriggerRequest
  ): Promise<BuildTriggerResponse> {
    const response: AxiosResponse<BuildTriggerResponse> = await this.client.post(
      `/api/v1/repositories/${repositoryId}/build-configs/${configId}/trigger`,
      request || {}
    );
    return response.data;
  }

  async createDefaultBuildConfiguration(repositoryId: number): Promise<BuildConfiguration> {
    const response: AxiosResponse<BuildConfiguration> = await this.client.post(
      `/api/v1/repositories/${repositoryId}/build-configs/create-default`
    );
    return response.data;
  }

  // ========================================
  // Pipeline depot -> build -> deploiement (V059)
  // ========================================
  //
  // L'intention vit sur le DEPLOIEMENT et s'edite depuis deux ecrans : sa fiche, et la
  // configuration de build du depot. Un seul stockage, deux portes d'entree -- deux
  // stockages divergeraient, et c'est ce qui a produit un `deploy_environment_id`
  // designant un environnement au lieu d'un deploiement.

  /** Les deploiements qui suivent ce depot (et cette branche), pour l'ecran de build. */
  async getDeploymentsBySource(
    connectedRepositoryId: number,
    branch?: string
  ): Promise<{ deploiements: DeploymentBySource[] }> {
    const response = await this.client.get<{ deploiements: DeploymentBySource[] }>(
      `/api/v1/deployments/by-source`,
      { params: { connected_repository_id: connectedRepositoryId, branch } }
    );
    return response.data;
  }

  /** Declare la source d'un deploiement et active ou non l'auto-deploiement. */
  async setAutoDeploy(
    deploymentId: string,
    payload: { enabled: boolean; connected_repository_id?: number; branch?: string }
  ): Promise<AutoDeployState> {
    const response = await this.client.patch<AutoDeployState>(
      `/api/v1/deployments/deployment/${deploymentId}/auto-deploy`,
      payload
    );
    return response.data;
  }

  /** Met en service l'image d'un build sur un deploiement : LE DEPOT ET LE TAG.
   *  Le serveur resout les deux depuis le build ; la console n'en compose aucun. */
  async deployBuild(
    buildId: string,
    deploymentId: string
  ): Promise<{ statut: string; message: string; depot?: string; image_tag?: string;
              changement_de_depot: boolean }> {
    const response = await this.client.post(
      `/api/v1/builds/${buildId}/deploy`,
      { deployment_id: deploymentId }
    );
    return response.data;
  }

  // ========================================
  // SQL Editor
  // ========================================

  async executeSqlQuery(deploymentId: string, query: string, database?: string): Promise<SqlExecuteResponse> {
    const response: AxiosResponse<SqlExecuteResponse> = await this.client.post(
      `/api/v1/sql-editor/deployment/${deploymentId}/execute`,
      { query, ...(database ? { database } : {}) }
    );
    return response.data;
  }

  async listSqlEditorDatabases(deploymentId: string): Promise<{databases: Array<{name: string; is_default: boolean; has_dedicated_user: boolean; user?: string}>}> {
    const response = await this.client.get<{databases: Array<{name: string; is_default: boolean; has_dedicated_user: boolean; user?: string}>}>(
      `/api/v1/sql-editor/deployment/${deploymentId}/databases`
    );
    return response.data;
  }

  async getSqlQueryResult(deploymentId: string, jobId: string): Promise<SqlResultResponse> {
    const response: AxiosResponse<SqlResultResponse> = await this.client.get(
      `/api/v1/sql-editor/deployment/${deploymentId}/result/${jobId}`
    );
    return response.data;
  }

  // ========================================
  // SQL Studio (per-session BFF pod, table explorer UI)
  // ========================================

  async openStudioSession(deploymentId: string, database?: string): Promise<StudioSessionResponse> {
    const response: AxiosResponse<StudioSessionResponse> = await this.client.post(
      `/api/v1/sql-editor/deployment/${deploymentId}/studio/session`,
      database ? { database } : {}
    );
    return response.data;
  }

  async closeStudioSession(deploymentId: string, sessionId: string): Promise<void> {
    await this.client.delete(
      `/api/v1/sql-editor/deployment/${deploymentId}/studio/session/${sessionId}`
    );
  }

  /**
   * Forwards a Studio protocol payload ({procedure, query|sequence}) through
   * the API to the user's BFF pod. Returns the raw Studio response shape
   * (`[err|null, rows]` or `[[err|null, rows1], [err|null, rows2]]`), already
   * unwrapped from the API envelope.
   */
  async studioProxyRequest(
    deploymentId: string,
    sessionId: string,
    payload: unknown
  ): Promise<unknown> {
    const response = await this.client.post(
      `/api/v1/sql-editor/deployment/${deploymentId}/studio/proxy/${sessionId}`,
      payload
    );
    // Backend returns { status_code, body }; Studio expects body directly.
    if (response.data && typeof response.data === 'object' && 'body' in response.data) {
      return (response.data as { body: unknown }).body;
    }
    return response.data;
  }

  // ========================================
  // Console Kafka
  // ========================================

  async openKafkaSession(deploymentId: string): Promise<KafkaSessionResponse> {
    const response: AxiosResponse<KafkaSessionResponse> = await this.client.post(
      `/api/v1/kafka-console/deployment/${deploymentId}/session`,
      {}
    );
    return response.data;
  }

  async closeKafkaSession(deploymentId: string): Promise<void> {
    await this.client.delete(`/api/v1/kafka-console/deployment/${deploymentId}/session`);
  }

  /**
   * Relaie un appel vers le BFF de la session. La méthode et le chemin passent
   * dans le corps, pas dans l'URL : le backend les valide contre une whitelist,
   * sans laquelle l'endpoint permettrait d'atteindre un chemin arbitraire du pod.
   *
   * Le backend renvoie { status_code, body } ; on rend `body` directement et on
   * lève si le BFF a répondu une erreur, pour que les appelants n'aient pas à
   * inspecter le code eux-mêmes.
   */
  async kafkaProxy<T = unknown>(
    deploymentId: string,
    method: 'GET' | 'POST' | 'DELETE',
    path: string,
    body?: unknown
  ): Promise<T> {
    const response = await this.client.post(
      `/api/v1/kafka-console/deployment/${deploymentId}/proxy`,
      { method, path, body: body ?? null }
    );
    const envelope = response.data as { status_code: number; body: unknown };
    if (envelope.status_code >= 400) {
      const detail =
        envelope.body && typeof envelope.body === 'object' && 'error' in envelope.body
          ? String((envelope.body as { error: unknown }).error)
          : `Erreur ${envelope.status_code}`;
      throw new Error(detail);
    }
    return envelope.body as T;
  }

  // ========================================
  // Audit Logs
  // ========================================

  async getAuditLogs(filters?: AuditLogFilters): Promise<AuditLogListResponse> {
    const params = new URLSearchParams();
    if (filters?.project_id) params.append('project_id', filters.project_id);
    if (filters?.action) params.append('action', filters.action);
    if (filters?.resource_type) params.append('resource_type', filters.resource_type);
    if (filters?.success !== undefined) params.append('success', String(filters.success));
    if (filters?.date_from) params.append('date_from', filters.date_from);
    if (filters?.date_to) params.append('date_to', filters.date_to);
    if (filters?.page) params.append('page', String(filters.page));
    if (filters?.page_size) params.append('page_size', String(filters.page_size));
    const qs = params.toString();
    const response: AxiosResponse<AuditLogListResponse> = await this.client.get(
      `/api/v1/audit-logs/${qs ? `?${qs}` : ''}`
    );
    return response.data;
  }

  async getProjectAuditLogs(projectId: string, filters?: AuditLogFilters): Promise<AuditLogListResponse> {
    const params = new URLSearchParams();
    if (filters?.action) params.append('action', filters.action);
    if (filters?.resource_type) params.append('resource_type', filters.resource_type);
    if (filters?.success !== undefined) params.append('success', String(filters.success));
    if (filters?.date_from) params.append('date_from', filters.date_from);
    if (filters?.date_to) params.append('date_to', filters.date_to);
    if (filters?.page) params.append('page', String(filters.page));
    if (filters?.page_size) params.append('page_size', String(filters.page_size));
    const qs = params.toString();
    const response: AxiosResponse<AuditLogListResponse> = await this.client.get(
      `/api/v1/audit-logs/projects/${projectId}${qs ? `?${qs}` : ''}`
    );
    return response.data;
  }

  async getMyAuditLogs(filters?: AuditLogFilters): Promise<AuditLogListResponse> {
    const params = new URLSearchParams();
    if (filters?.action) params.append('action', filters.action);
    if (filters?.resource_type) params.append('resource_type', filters.resource_type);
    if (filters?.success !== undefined) params.append('success', String(filters.success));
    if (filters?.date_from) params.append('date_from', filters.date_from);
    if (filters?.date_to) params.append('date_to', filters.date_to);
    if (filters?.page) params.append('page', String(filters.page));
    if (filters?.page_size) params.append('page_size', String(filters.page_size));
    const qs = params.toString();
    const response: AxiosResponse<AuditLogListResponse> = await this.client.get(
      `/api/v1/audit-logs/me${qs ? `?${qs}` : ''}`
    );
    return response.data;
  }

  // SSE Ticket — one-time ticket for SSE connections (replaces token in URL)
  async createSseTicket(): Promise<string> {
    const response = await this.client.post<{ ticket: string }>('/api/v1/logs/create-sse-ticket');
    return response.data.ticket;
  }

  // ==========================================================
  // NubiS3 — S3-as-a-service via Backblaze B2
  // ==========================================================

  async listS3Buckets(organisationId: string): Promise<import('../types/api').S3Bucket[]> {
    const response: AxiosResponse<import('../types/api').S3Bucket[]> = await this.client.get(
      `/api/v1/s3/buckets`,
      { params: { organisation_id: organisationId } }
    );
    return response.data;
  }

  async getS3Bucket(bucketId: string): Promise<import('../types/api').S3Bucket> {
    const response: AxiosResponse<import('../types/api').S3Bucket> = await this.client.get(
      `/api/v1/s3/buckets/${bucketId}`
    );
    return response.data;
  }

  async createS3Bucket(
    payload: import('../types/api').S3BucketCreate
  ): Promise<import('../types/api').S3Bucket> {
    const response: AxiosResponse<import('../types/api').S3Bucket> = await this.client.post(
      `/api/v1/s3/buckets`,
      payload
    );
    return response.data;
  }

  async deleteS3Bucket(bucketId: string): Promise<void> {
    await this.client.delete(`/api/v1/s3/buckets/${bucketId}`);
  }

  async listS3AccessKeys(bucketId: string): Promise<import('../types/api').S3AccessKey[]> {
    const response: AxiosResponse<import('../types/api').S3AccessKey[]> = await this.client.get(
      `/api/v1/s3/buckets/${bucketId}/access-keys`
    );
    return response.data;
  }

  /** Crée une access key et retourne le secret — affiché UNE SEULE FOIS au user. */
  async createS3AccessKey(
    bucketId: string,
    payload: import('../types/api').S3AccessKeyCreate
  ): Promise<import('../types/api').S3AccessKeySecret> {
    const response: AxiosResponse<import('../types/api').S3AccessKeySecret> = await this.client.post(
      `/api/v1/s3/buckets/${bucketId}/access-keys`,
      payload
    );
    return response.data;
  }

  async revokeS3AccessKey(bucketId: string, keyId: string): Promise<void> {
    await this.client.delete(`/api/v1/s3/buckets/${bucketId}/access-keys/${keyId}`);
  }

  // Objects (files)
  async listS3Objects(
    bucketId: string,
    options: { prefix?: string; cursor?: string; max?: number } = {}
  ): Promise<import('../types/api').S3ObjectListResponse> {
    const response: AxiosResponse<import('../types/api').S3ObjectListResponse> = await this.client.get(
      `/api/v1/s3/buckets/${bucketId}/objects`,
      { params: { prefix: options.prefix ?? '', cursor: options.cursor ?? '', max: options.max ?? 100 } }
    );
    return response.data;
  }

  async getS3UploadUrl(
    bucketId: string,
    payload: import('../types/api').S3PresignedUploadRequest
  ): Promise<import('../types/api').S3PresignedUrlResponse> {
    const response: AxiosResponse<import('../types/api').S3PresignedUrlResponse> = await this.client.post(
      `/api/v1/s3/buckets/${bucketId}/objects/upload-url`,
      payload
    );
    return response.data;
  }

  async getS3DownloadUrl(
    bucketId: string,
    key: string
  ): Promise<import('../types/api').S3PresignedUrlResponse> {
    const response: AxiosResponse<import('../types/api').S3PresignedUrlResponse> = await this.client.get(
      `/api/v1/s3/buckets/${bucketId}/objects/download-url`,
      { params: { key } }
    );
    return response.data;
  }

  async deleteS3Object(bucketId: string, key: string): Promise<void> {
    await this.client.delete(`/api/v1/s3/buckets/${bucketId}/objects`, { data: { key } });
  }

  // ============================================================================
  // BLUEPRINT / STACK ENDPOINTS (nubicloud.yaml multi-services)
  // ============================================================================

  async detectBlueprint(payload: DetectRequest): Promise<DetectResponse> {
    const response: AxiosResponse<DetectResponse> = await this.client.post(
      '/api/v1/blueprints/detect',
      payload
    );
    return response.data;
  }

  async planBlueprint(environmentId: string, manifest: object): Promise<StackPlan> {
    const response: AxiosResponse<StackPlan> = await this.client.post('/api/v1/blueprints/plan', {
      environment_id: environmentId,
      manifest,
    });
    return response.data;
  }

  async applyBlueprint(
    environmentId: string,
    manifest: object,
    opts?: { connected_repository_id?: number; manifest_source?: string }
  ): Promise<StackSummary> {
    const response: AxiosResponse<StackSummary> = await this.client.post('/api/v1/blueprints/apply', {
      environment_id: environmentId,
      manifest,
      ...(opts?.connected_repository_id !== undefined ? { connected_repository_id: opts.connected_repository_id } : {}),
      ...(opts?.manifest_source !== undefined ? { manifest_source: opts.manifest_source } : {}),
    });
    return response.data;
  }

  async getStack(stackId: string): Promise<StackSummary> {
    const response: AxiosResponse<StackSummary> = await this.client.get(`/api/v1/blueprints/stacks/${stackId}`);
    return response.data;
  }

  async listStacks(environmentId: string): Promise<StackSummary[]> {
    const response: AxiosResponse<StackSummary[]> = await this.client.get(
      `/api/v1/blueprints/environments/${environmentId}/stacks`
    );
    return response.data;
  }

  async reconcileStack(stackId: string, dry = false): Promise<StackSummary> {
    const response: AxiosResponse<StackSummary> = await this.client.post(
      `/api/v1/blueprints/stacks/${stackId}/reconcile?dry=${dry}`
    );
    return response.data;
  }

  // Relance ciblée : réinitialise + redéploie les briques non-LIVE (coincées/en échec)
  async retryStack(stackId: string): Promise<StackSummary> {
    const response: AxiosResponse<StackSummary> = await this.client.post(
      `/api/v1/blueprints/stacks/${stackId}/retry`
    );
    return response.data;
  }

  // Supprime le stack + toutes ses ressources (DB managées + apps)
  async deleteStack(stackId: string): Promise<void> {
    await this.client.delete(`/api/v1/blueprints/stacks/${stackId}`);
  }

  // Ré-applique un manifeste sur un stack existant (mise à jour)
  async updateStack(stackId: string, environmentId: string, manifest: object): Promise<StackSummary> {
    const response: AxiosResponse<StackSummary> = await this.client.put(
      `/api/v1/blueprints/stacks/${stackId}`,
      { environment_id: environmentId, manifest }
    );
    return response.data;
  }

  // ==========================================================================
  // Config Files (feature 2026-08-06)
  // Fichiers de configuration custom montés dans le container d'un deployment.
  // Whitelist paths cote backend : /app, /etc/app, /config, /opt, /mnt, /data,
  // /srv, /workspace. Max 10 fichiers x 100 KB, 900 KB total.
  // ==========================================================================

  async getDeploymentConfigFiles(deploymentId: string): Promise<ConfigFile[]> {
    const response: AxiosResponse<ConfigFile[]> = await this.client.get(
      `/api/v1/deployments/deployment/${deploymentId}/config-files`
    );
    return response.data;
  }

  async updateDeploymentConfigFiles(
    deploymentId: string,
    files: ConfigFileInput[]
  ): Promise<ConfigFile[]> {
    const response: AxiosResponse<ConfigFile[]> = await this.client.put(
      `/api/v1/deployments/deployment/${deploymentId}/config-files`,
      { files }
    );
    return response.data;
  }

  async deleteDeploymentConfigFile(deploymentId: string, fileId: string): Promise<void> {
    await this.client.delete(
      `/api/v1/deployments/deployment/${deploymentId}/config-files/${fileId}`
    );
  }

  // Utilitaires
  isAuthenticated(): boolean {
    return !!this.token;
  }

  // ─── NubieAI : generateur de sites vitrines ───
  // Tout fonctionne sans cle de fournisseur SAUF sendNubieAiMessage, qui rend un 503
  // explicite -- l'ecran peut donc proposer le parcours complet jusqu'au choix du style.

  async getNubieAiDirections(): Promise<NubieAiDirection[]> {
    const response: AxiosResponse<NubieAiDirection[]> = await this.client.get(
      '/api/v1/nubie-ai/directions'
    );
    return response.data;
  }

  /**
   * Les actifs du socle, pour que l'apercu soit STYLE.
   *
   * La page generee lie `/_nubie/base.css`, un chemin que seul le site publie
   * resoudra : dans une iframe de la console il ne resout rien, et le client verrait
   * une page nue en concluant que NubieAI a mal travaille. On les incorpore donc.
   */
  /**
   * Met le site en ligne. SYNCHRONE : l'appel attend le televersement complet.
   *
   * Les refus sont distincts et appellent trois gestes differents : 422 = il manque
   * quelque chose au site (style, page, taille), 409 = l'adresse est prise, 502 = la
   * mise en ligne a echoue mais le travail est conserve.
   */
  async publishNubieAiSite(siteId: string): Promise<NubieAiResultatPublication> {
    const response: AxiosResponse<NubieAiResultatPublication> = await this.client.post(
      `/api/v1/nubie-ai/sites/${siteId}/publier`
    );
    return response.data;
  }

  /** Retire le site de la ligne. Le brouillon reste intact, et le slug reste reserve. */
  async unpublishNubieAiSite(siteId: string): Promise<void> {
    await this.client.post(`/api/v1/nubie-ai/sites/${siteId}/depublier`);
  }

  /**
   * Supprime DEFINITIVEMENT un site : sa page, ses photos, son adresse, sa ligne.
   *
   * A NE PAS CONFONDRE AVEC `unpublishNubieAiSite`, et la difference n'est pas de degre.
   * Depublier retire le site du web et garde tout : le brouillon, les photos, le slug et
   * meme le certificat, pour qu'une republication ne redemande rien. Supprimer n'en
   * garde rien, et rien ne se rattrape -- d'ou la confirmation a l'ecran.
   *
   * Un 502 veut dire que le retrait du site EN LIGNE n'a pas abouti : le site existe
   * toujours et reste supprimable. C'est voulu -- retirer la ligne alors que les objets
   * sont encore servis laisserait un site en ligne que plus personne ne possede.
   */
  async deleteNubieAiSite(siteId: string): Promise<void> {
    await this.client.delete(`/api/v1/nubie-ai/sites/${siteId}`);
  }

  // ---------------------------------------------------------------------------
  // Les photos du site (lot 7)
  // ---------------------------------------------------------------------------

  async listNubieAiMedias(siteId: string): Promise<NubieAiMedia[]> {
    const response: AxiosResponse<NubieAiMedia[]> = await this.client.get(
      `/api/v1/nubie-ai/sites/${siteId}/medias`
    );
    return response.data;
  }

  /**
   * Televerse une photo. L'API la RE-ENCODE : elle purge l'EXIF -- donc les
   * coordonnees GPS d'une photo de telephone -- applique l'orientation et reduit le
   * poids. Le fichier rendu n'est jamais celui envoye.
   *
   * `slot` et `alt` voyagent en CHAMPS DE FORMULAIRE et non dans l'URL : un texte
   * alternatif ecrit par le client atterrirait sinon dans les journaux d'acces.
   *
   * Les refus sont distincts et appellent des gestes differents : 413 = trop lourde,
   * 422 = format refuse (SVG, fichier qui n'est pas une image, dimensions
   * demesurees) ou quota atteint, 502 = l'envoi a echoue mais le site est conserve.
   */
  async uploadNubieAiMedia(
    siteId: string,
    file: File,
    options?: { slot?: string | null; alt?: string }
  ): Promise<NubieAiMedia> {
    const formData = new FormData();
    formData.append('fichier', file);
    if (options?.slot) formData.append('slot', options.slot);
    if (options?.alt !== undefined) formData.append('alt', options.alt);

    const response: AxiosResponse<NubieAiMedia> = await this.client.post(
      `/api/v1/nubie-ai/sites/${siteId}/medias`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return response.data;
  }

  /**
   * Les octets d'une photo, pour l'afficher avant publication.
   *
   * POURQUOI UN BLOB ET NON UN `src`. L'original vit dans un bucket PRIVE sous un
   * prefixe que le proxy ne sert pas : il n'existe aucune URL publique avant
   * publication. L'API sert donc l'apercu, mais elle est sur une AUTRE ORIGINE que la
   * console -- une balise `<img src>` ne porterait pas le cookie httpOnly. Il faut
   * passer par le client axios, qui envoie les identifiants.
   *
   * L'appelant DOIT appeler `URL.revokeObjectURL` quand il n'en a plus besoin : sans
   * ca, chaque affichage retient ses octets jusqu'au rechargement de la page.
   */
  async getNubieAiMediaBlob(siteId: string, mediaId: string): Promise<Blob> {
    const response: AxiosResponse<Blob> = await this.client.get(
      `/api/v1/nubie-ai/sites/${siteId}/medias/${mediaId}/contenu`,
      { responseType: 'blob' }
    );
    return response.data;
  }

  /**
   * Pose une photo dans un emplacement, ou change son texte alternatif.
   *
   * `slot` ABSENT ne touche a rien ; `slot: ''` RETIRE la photo de son emplacement.
   * Confondre les deux rendrait le depositionnement impossible.
   */
  async updateNubieAiMedia(
    siteId: string,
    mediaId: string,
    payload: { slot?: string | null; alt?: string }
  ): Promise<NubieAiMedia> {
    const response: AxiosResponse<NubieAiMedia> = await this.client.put(
      `/api/v1/nubie-ai/sites/${siteId}/medias/${mediaId}`,
      payload
    );
    return response.data;
  }

  /** Retire la photo, sa ligne et son original. Irreversible. */
  async deleteNubieAiMedia(siteId: string, mediaId: string): Promise<void> {
    await this.client.delete(`/api/v1/nubie-ai/sites/${siteId}/medias/${mediaId}`);
  }

  async getNubieAiSocle(): Promise<NubieAiSocle> {
    const response: AxiosResponse<NubieAiSocle> = await this.client.get(
      '/api/v1/nubie-ai/socle'
    );
    return response.data;
  }

  async getNubieAiSites(organisationId: string): Promise<NubieAiSiteResume[]> {
    const response: AxiosResponse<NubieAiSiteResume[]> = await this.client.get(
      '/api/v1/nubie-ai/sites',
      { params: { organisation_id: organisationId } }
    );
    return response.data;
  }

  async createNubieAiSite(
    organisationId: string,
    payload: { nom: string; brief?: string; direction?: string; contact?: NubieAiContact }
  ): Promise<NubieAiSiteDetail> {
    const response: AxiosResponse<NubieAiSiteDetail> = await this.client.post(
      '/api/v1/nubie-ai/sites',
      payload,
      { params: { organisation_id: organisationId } }
    );
    return response.data;
  }

  async getNubieAiSite(siteId: string): Promise<NubieAiSiteDetail> {
    const response: AxiosResponse<NubieAiSiteDetail> = await this.client.get(
      `/api/v1/nubie-ai/sites/${siteId}`
    );
    return response.data;
  }

  async updateNubieAiSite(
    siteId: string,
    payload: { nom?: string; brief?: string; direction?: string; contact?: NubieAiContact }
  ): Promise<NubieAiSiteDetail> {
    const response: AxiosResponse<NubieAiSiteDetail> = await this.client.put(
      `/api/v1/nubie-ai/sites/${siteId}`,
      payload
    );
    return response.data;
  }

  async sendNubieAiMessage(siteId: string, message: string): Promise<NubieAiResultatMessage> {
    const response: AxiosResponse<NubieAiResultatMessage> = await this.client.post(
      `/api/v1/nubie-ai/sites/${siteId}/messages`,
      { message }
    );
    return response.data;
  }

  /**
   * Le fil de discussion du site, du plus ancien au plus recent.
   *
   * POURQUOI UNE ROUTE A PART, et non un champ de la fiche : le fil grandit a chaque
   * tour et la fiche est relue a chaque changement de photo, de style ou de
   * publication. L'embarquer ferait dechiffrer tout l'historique a chacune de ces
   * lectures -- cote serveur la relation est en `lazy="select"` pour cette raison.
   *
   * CE QUE CETTE ROUTE REND N'EST PAS CE QUE LE MODELE SE RAPPELLE : ses bornes sont
   * celles d'une reponse HTTP, pas celles d'une facture de jetons. Un tour peut donc
   * s'afficher alors que le modele l'a oublie -- c'est le bon sens du compromis.
   */
  async getNubieAiMessages(siteId: string): Promise<NubieAiTour[]> {
    const response: AxiosResponse<NubieAiTour[]> = await this.client.get(
      `/api/v1/nubie-ai/sites/${siteId}/messages`
    );
    return response.data;
  }

  /**
   * Le site repond-il VRAIMENT ? A sonder apres une publication.
   *
   * L'API lit la condition `Ready` du `Certificate`, pas la page : sonder l'URL
   * publique confondrait trop de causes -- DNS, egress, cache du proxy -- et un
   * echec de resolution se lirait comme « site mort » alors que la requete n'est
   * jamais partie.
   */
  async getNubieAiEtatEnLigne(siteId: string): Promise<NubieAiEtatEnLigne> {
    const response: AxiosResponse<NubieAiEtatEnLigne> = await this.client.get(
      `/api/v1/nubie-ai/sites/${siteId}/en-ligne`
    );
    return response.data;
  }

  getToken(): string | null {
    // Token is no longer stored client-side; auth is via httpOnly cookie
    return this.token;
  }
}

// Instance singleton
export const apiService = new ApiService();
export default apiService;